import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Volume2, BookOpen, Play, Lightbulb, Users, Heart, Star, Baby, Palette, Music, Clock, Eye, Award, Brain, Shield, Smile, Target, CheckCircle, Home } from 'lucide-react';

// Activité 1 bis - Éveil et loisirs
// Mise en œuvre d'activités d'éveil, de loisirs, d'éducation et d'accompagnement à la vie sociale adaptées à l'enfant ou au groupe

const QUIZ_EVEIL = [
  {
    question: "Selon Virginia Henderson, quel besoin fondamental le jeu permet-il de satisfaire ?",
    options: ["Respirer", "Se divertir", "Se vêtir", "Dormir"],
    correct: 1,
    explanation: "Le besoin de se divertir fait partie des 14 besoins fondamentaux identifiés par Virginia Henderson. Le jeu y répond directement."
  },
  {
    question: "À quel âge l'enfant développe-t-il le jeu symbolique (faire semblant) ?",
    options: ["0-6 mois", "6-12 mois", "18 mois - 3 ans", "4-5 ans"],
    correct: 2,
    explanation: "Entre 18 mois et 3 ans, l'enfant développe le jeu symbolique : il imite, fait semblant, et se positionne dans des rôles."
  },
  {
    question: "Qu'est-ce que la motricité libre selon Emmi Pikler ?",
    options: ["Forcer l'enfant à marcher", "Laisser l'enfant explorer sans mise en position forcée", "Faire des exercices de gym", "Utiliser un trotteur"],
    correct: 1,
    explanation: "La motricité libre selon Pikler consiste à ne pas mettre l'enfant dans une position qu'il n'a pas acquise lui-même."
  },
  {
    question: "Quel est le rôle de l'AP pendant le jeu libre ?",
    options: ["Diriger les activités", "Observer sans diriger et garantir la sécurité", "Jouer à la place de l'enfant", "Imposer des règles strictes"],
    correct: 1,
    explanation: "L'AP observe sans envahir, garantit la sécurité, met des mots sur les émotions et régule les conflits. Elle est facilitatrice, pas animatrice permanente."
  },
  {
    question: "Les activités de transvasement développent principalement :",
    options: ["Uniquement la force physique", "La motricité fine et la concentration", "La lecture", "Le langage écrit"],
    correct: 1,
    explanation: "Le transvasement développe la motricité fine, la coordination main-œil, la concentration et prépare à l'écriture."
  }
];

const QUIZ_FINAL_10 = [
  {
    question: "Selon Virginia Henderson, quel besoin fondamental le jeu permet-il principalement de satisfaire ?",
    options: ["Respirer", "Se divertir", "Se vêtir", "Dormir"],
    correct: 1,
    explanation: "Le besoin de « se divertir » fait partie des 14 besoins fondamentaux identifiés par Virginia Henderson. Le jeu y répond directement."
  },
  {
    question: "À quel âge l'enfant développe-t-il le jeu symbolique (faire semblant) ?",
    options: ["0-6 mois", "6-12 mois", "18 mois - 3 ans", "4-5 ans"],
    correct: 2,
    explanation: "Entre 18 mois et 3 ans, l'enfant développe le jeu symbolique : il imite, fait semblant, et se positionne dans des rôles. Cela participe à la construction du « Je »."
  },
  {
    question: "Qu'est-ce que la motricité libre selon Emmi Pikler ?",
    options: ["Forcer l'enfant à marcher tôt", "Laisser l'enfant explorer sans mise en position forcée", "Faire des exercices de gym avec l'enfant", "Utiliser un trotteur pour accélérer la marche"],
    correct: 1,
    explanation: "La motricité libre selon Pikler consiste à ne pas mettre l'enfant dans une position qu'il n'a pas acquise lui-même. L'enfant explore à son rythme."
  },
  {
    question: "Quel est le rôle de l'auxiliaire de puériculture pendant le jeu libre ?",
    options: ["Diriger les activités et imposer des règles strictes", "Observer sans diriger et garantir la sécurité", "Jouer à la place de l'enfant", "Corriger immédiatement les erreurs de l'enfant"],
    correct: 1,
    explanation: "L'AP observe sans envahir, garantit la sécurité, met des mots sur les émotions et régule les conflits. Elle est facilitatrice, pas animatrice permanente."
  },
  {
    question: "Les activités de transvasement développent principalement :",
    options: ["Uniquement la force physique", "La motricité fine, la coordination main-œil et la concentration", "La lecture et l'écriture", "Le langage oral exclusivement"],
    correct: 1,
    explanation: "Le transvasement développe la motricité fine, la coordination main-œil, la concentration et prépare indirectement à l'écriture."
  },
  {
    question: "Quel aménagement est adapté pour un bébé de 0-12 mois ?",
    options: ["Un parcours motricité avec obstacles hauts", "Un tapis ferme avec miroir sécurisé et hochets adaptés", "Un coin déguisement et cuisine", "Des jeux de construction complexes"],
    correct: 1,
    explanation: "Pour les 0-12 mois, on privilégie la motricité libre avec un tapis ferme, des miroirs sécurisés et des objets légers. Pas de mise en position forcée."
  },
  {
    question: "Pourquoi les rituels sont-ils importants en EAJE ?",
    options: ["Pour occuper le temps de façon aléatoire", "Pour structurer le temps et donner des repères sécurisants", "Pour fatiguer l'enfant avant la sieste", "Pour remplacer le jeu libre"],
    correct: 1,
    explanation: "Les rituels structurent le temps, donnent des repères sécurisants, favorisent la cohésion du groupe et répondent au besoin de sécurité affective (Maslow)."
  },
  {
    question: "À quel âge la permanence de l'objet apparaît-elle selon Piaget ?",
    options: ["0-3 mois", "3-6 mois", "6-12 mois (vers 8-9 mois)", "2-3 ans"],
    correct: 2,
    explanation: "Selon Piaget, la permanence de l'objet (comprendre qu'un objet existe même quand on ne le voit plus) apparaît entre 6 et 12 mois, généralement vers 8-9 mois."
  },
  {
    question: "Quelle norme garantit la sécurité des jouets en France ?",
    options: ["Norme ISO 9001", "Norme NF EN 71", "Norme AFNOR 2000", "Le marquage CE suffit"],
    correct: 1,
    explanation: "La norme NF EN 71 est la norme européenne de sécurité des jouets. Elle est complémentaire au marquage CE et garantit l'absence de risques pour les enfants."
  },
  {
    question: "Avant 3 ans, pourquoi le partage est-il difficile pour l'enfant ?",
    options: ["Parce qu'il est méchant", "Parce qu'il est encore centré sur lui-même (égocentrisme selon Piaget)", "Parce qu'il ne comprend pas le langage", "Parce qu'il n'a pas de jouets"],
    correct: 1,
    explanation: "Avant 3 ans, le partage n'est pas acquis car l'enfant est encore centré sur lui-même (égocentrisme selon Piaget). L'AP peut utiliser le tour de rôle et le minuteur visuel pour l'accompagner."
  }
];

const SLIDES = [
  // SLIDE 1 - Titre
  {
    id: 1,
    type: 'title',
    title: "Activité 1 bis",
    subtitle: "Mise en œuvre d'activités d'éveil, de loisirs, d'éducation et d'accompagnement à la vie sociale adaptées à l'enfant ou au groupe",
    icon: Lightbulb,
    color: "#8B5CF6",
    narration: "Bienvenue dans l'Activité 1 bis du DEAP. Cette activité porte sur la mise en œuvre d'activités d'éveil, de loisirs, d'éducation et d'accompagnement à la vie sociale adaptées à l'enfant ou au groupe."
  },
  // SLIDE 2 - Sommaire
  {
    id: 2,
    type: 'table_of_contents',
    title: "Sommaire de la Formation",
    color: "#8B5CF6",
    sections: [
      { title: "1. Objectifs de l'activité", slides: "3-4", targetSlide: 3, color: "#8B5CF6", icon: "Target" },
      { title: "2. Cadre théorique", slides: "5-8", targetSlide: 5, color: "#3B82F6", icon: "Brain" },
      { title: "3. Le jeu et l'identité", slides: "9-18", targetSlide: 9, color: "#10B981", icon: "Puzzle" },
      { title: "4. Activités encadrées", slides: "19-23", targetSlide: 19, color: "#F59E0B", icon: "Palette" },
      { title: "5. Transvasement", slides: "24", targetSlide: 24, color: "#8B5CF6", icon: "Droplet" },
      { title: "6. Attentes du jury", slides: "25-26", targetSlide: 25, color: "#F97316", icon: "Award" },
      { title: "7. Rituels et regroupement", slides: "27-28", targetSlide: 27, color: "#EC4899", icon: "Clock" },
      { title: "8. Le jeu libre", slides: "29", targetSlide: 29, color: "#6366F1", icon: "Play" },
      { title: "9. Aménagement de l'espace", slides: "30-51", targetSlide: 30, color: "#06B6D4", icon: "Home" },
      { title: "10. Le jeu libre approfondi", slides: "52-63", targetSlide: 52, color: "#6366F1", icon: "Smile" },
      { title: "11. L'éveil des bébés", slides: "64-68", targetSlide: 64, color: "#14B8A6", icon: "Baby" },
      { title: "12. Vie sociale", slides: "69-70", targetSlide: 69, color: "#EF4444", icon: "Users" },
      { title: "13. Synthèse", slides: "71", targetSlide: 71, color: "#EC4899", icon: "Star" },
      { title: "14. Quiz final", slides: "72", targetSlide: 72, color: "#22C55E", icon: "CheckCircle" },
      { title: "15. Conclusion", slides: "73", targetSlide: 73, color: "#A855F7", icon: "Award" }
    ],
    narration: "Voici le sommaire de cette formation. Cliquez sur une section pour y accéder directement. Vous avez un quiz à la fin pour valider vos acquis."
  },
  // SLIDE 3 - Objectifs
  {
    id: 3,
    type: 'section',
    title: "Objectifs de l'Activité 1 bis",
    subtitle: "Ce que l'auxiliaire de puériculture doit maîtriser",
    icon: Target,
    color: "#8B5CF6",
    narration: "Découvrons les objectifs que vous devez atteindre dans cette activité."
  },
  // SLIDE 4 - Liste objectifs
  {
    id: 4,
    type: 'objectives',
    title: "L'AP doit être capable de :",
    color: "#8B5CF6",
    objectives: [
      { icon: "🎯", text: "Proposer des activités adaptées à l'âge et au développement" },
      { icon: "🧠", text: "Favoriser l'éveil global (moteur, cognitif, sensoriel, affectif, social)" },
      { icon: "👁️", text: "Observer les compétences et fragilités" },
      { icon: "👩‍⚕️", text: "Adapter sa posture professionnelle" },
      { icon: "👥", text: "Favoriser la socialisation et la vie en groupe" },
      { icon: "❤️", text: "Respecter la singularité de chaque enfant" }
    ],
    narration: "L'auxiliaire de puériculture doit proposer des activités adaptées, favoriser l'éveil global, observer, adapter sa posture, favoriser la socialisation et respecter la singularité de chaque enfant."
  },
  // SLIDE 5 - Cadre théorique
  {
    id: 5,
    type: 'section',
    title: "Cadre Théorique",
    subtitle: "Les références fondamentales",
    icon: Brain,
    color: "#3B82F6",
    narration: "Passons maintenant au cadre théorique qui soutient notre pratique professionnelle."
  },
  // SLIDE 6 - Virginia Henderson
  {
    id: 6,
    type: 'theory',
    title: "Besoins fondamentaux (Virginia Henderson)",
    color: "#3B82F6",
    theorist: "Virginia Henderson",
    points: [
      { icon: "🎮", text: "Se divertir", highlight: true },
      { icon: "💬", text: "Communiquer", highlight: true },
      { icon: "🏃", text: "Se mouvoir", highlight: true },
      { icon: "📚", text: "Apprendre", highlight: true },
      { icon: "🛡️", text: "Être en sécurité", highlight: true }
    ],
    note: "Ces 5 besoins sont particulièrement sollicités dans les activités d'éveil",
    narration: "Virginia Henderson a identifié 14 besoins fondamentaux. Pour l'éveil, 5 sont essentiels : se divertir, communiquer, se mouvoir, apprendre et être en sécurité."
  },
  // SLIDE 7 - Maslow
  {
    id: 7,
    type: 'pyramid',
    title: "Pyramide de Maslow",
    color: "#3B82F6",
    levels: [
      { level: "Accomplissement", color: "#8B5CF6", description: "Réaliser son potentiel" },
      { level: "Estime", color: "#EC4899", description: "Confiance, reconnaissance" },
      { level: "Appartenance", color: "#F59E0B", description: "Groupe, relations sociales" },
      { level: "Sécurité", color: "#10B981", description: "Protection, stabilité" },
      { level: "Physiologiques", color: "#6366F1", description: "Besoins de base" }
    ],
    narration: "La pyramide de Maslow montre la hiérarchie des besoins. Pour l'éveil, les besoins de sécurité, d'appartenance, d'estime et d'accomplissement sont fondamentaux."
  },
  // SLIDE 8 - Références développementales
  {
    id: 8,
    type: 'references',
    title: "Références développementales",
    color: "#3B82F6",
    references: [
      { 
        author: "Henri Wallon", 
        years: "1879-1962",
        nationality: "Français",
        profession: "Psychologue et médecin",
        concept: "Développement psychomoteur", 
        description: "L'enfant se développe par le mouvement et l'action",
        bio: "Pionnier de la psychologie de l'enfant en France, il a montré l'importance du corps et des émotions dans le développement cognitif."
      },
      { 
        author: "John Bowlby", 
        years: "1907-1990",
        nationality: "Britannique",
        profession: "Psychiatre et psychanalyste",
        concept: "Attachement", 
        description: "La sécurité affective comme base d'exploration",
        bio: "Père de la théorie de l'attachement, il a démontré le besoin fondamental du nourrisson d'établir un lien affectif stable avec une figure d'attachement."
      },
      { 
        author: "Emmi Pikler", 
        years: "1902-1984",
        nationality: "Hongroise",
        profession: "Pédiatre",
        concept: "Motricité libre", 
        description: "Ne pas mettre l'enfant en position qu'il n'a pas acquise",
        bio: "Fondatrice de la pouponnière de Lóczy à Budapest, elle a développé une approche respectueuse du rythme naturel de développement de l'enfant."
      },
      { 
        author: "Jean Piaget", 
        years: "1896-1980",
        nationality: "Suisse",
        profession: "Psychologue et biologiste",
        concept: "Stade sensori-moteur", 
        description: "L'intelligence se construit par l'expérimentation (0-2 ans)",
        bio: "Fondateur de l'épistémologie génétique, il a décrit les stades du développement cognitif de l'enfant, de la naissance à l'adolescence."
      }
    ],
    narration: "Plusieurs théoriciens guident notre pratique : Henri Wallon pour le développement psychomoteur, John Bowlby pour l'attachement, Emmi Pikler pour la motricité libre, et Jean Piaget pour le stade sensori-moteur."
  },
  // SLIDE 9 - Le jeu section
  {
    id: 9,
    type: 'section_illustrated',
    title: "À travers le jeu, l'enfant devient « Je »",
    subtitle: "Le jeu comme construction de l'identité",
    icon: Smile,
    color: "#10B981",
    image: "https://customer-assets.emergentagent.com/job_7319947e-f6fb-47e1-823c-3efab0745ed3/artifacts/cu14u2wl_image.png",
    narration: "Le jeu n'est pas une simple activité occupationnelle. C'est le lieu privilégié où l'enfant se construit et devient progressivement un sujet à part entière."
  },
  // SLIDE 10 - Le jeu n'est pas occupationnel
  {
    id: 10,
    type: 'concept',
    title: "Le jeu : bien plus qu'une occupation",
    color: "#10B981",
    mainText: "Le jeu n'est pas une simple activité occupationnelle.",
    subText: "Il est le lieu privilégié où l'enfant se construit, s'exprime et devient progressivement un sujet à part entière.",
    highlight: "À travers le jeu, l'enfant ne fait pas seulement une action : il expérimente son pouvoir d'agir sur le monde.",
    conclusion: "C'est dans cette expérience qu'il passe du « on fait pour moi » au « je fais ».",
    narration: "Le jeu permet à l'enfant d'expérimenter son pouvoir d'agir sur le monde. C'est ainsi qu'il passe du 'on fait pour moi' au 'je fais'."
  },
  // SLIDE 11 - Construction identité
  {
    id: 11,
    type: 'content',
    title: "Le jeu comme construction de l'identité",
    color: "#10B981",
    intro: "Dès les premiers mois, le bébé explore son environnement par le toucher, la bouche, le regard.",
    points: [
      { icon: "👋", text: "Il découvre qu'il peut agir : attraper, secouer, faire tomber, recommencer" },
      { icon: "🔄", text: "Cette répétition n'est pas anodine : elle lui permet d'intégrer une notion fondamentale" }
    ],
    highlight: "👉 « C'est moi qui provoque cela. »",
    conclusion: "Ce sentiment d'agentivité est la première étape vers la construction du « Je ».",
    narration: "Dès les premiers mois, le bébé explore et découvre qu'il peut agir sur le monde. Cette découverte de son pouvoir d'action est la première étape vers la construction de son identité."
  },
  // SLIDE 12 - Conscience de soi
  {
    id: 12,
    type: 'development',
    title: "Le jeu et la conscience de soi",
    color: "#10B981",
    ageRange: "18 mois - 3 ans",
    developments: [
      { icon: "🪞", text: "La reconnaissance de son image" },
      { icon: "🚫", text: "L'opposition (« non ! »)" },
      { icon: "✋", text: "L'affirmation de ses choix" },
      { icon: "🎭", text: "Le jeu symbolique" }
    ],
    explanation: "Lorsqu'il joue à faire semblant (donner à manger à la poupée, imiter un adulte), il ne copie pas seulement : il se positionne.",
    keyPoints: [
      "Il expérimente des rôles",
      "Il teste des identités",
      "Il affirme une place"
    ],
    conclusion: "Le jeu symbolique participe directement à la construction du « Je ».",
    narration: "Entre 18 mois et 3 ans, l'enfant développe la conscience de soi à travers la reconnaissance de son image, l'opposition, l'affirmation de ses choix et le jeu symbolique."
  },
  // SLIDE 13 - Jeu libre
  {
    id: 13,
    type: 'freeplay',
    title: "Le jeu libre : un espace d'affirmation",
    color: "#10B981",
    intro: "Dans le jeu libre, l'enfant :",
    actions: [
      { icon: "✓", text: "Choisit" },
      { icon: "✓", text: "Décide" },
      { icon: "✓", text: "Organise" },
      { icon: "✓", text: "Imagine" },
      { icon: "✓", text: "Résout" }
    ],
    highlight: "Il n'est plus uniquement dirigé par l'adulte. Il devient acteur.",
    adultRole: "L'adulte sécurise, mais ne dirige pas.",
    conclusion: "Ce cadre sécurisant permet à l'enfant d'oser être lui-même.",
    narration: "Dans le jeu libre, l'enfant devient acteur : il choisit, décide, organise, imagine et résout. L'adulte sécurise sans diriger."
  },
  // SLIDE 14 - Sécurité affective
  {
    id: 14,
    type: 'attachment',
    title: "Le jeu et la sécurité affective",
    color: "#10B981",
    mainConcept: "Un enfant ne peut devenir « Je » que s'il se sent en sécurité.",
    theory: "La théorie de l'attachement (Bowlby) montre que la présence stable d'un adulte permet à l'enfant d'explorer le monde.",
    keyPoint: "L'adulte devient une base sécurisante.",
    explanation: "L'enfant s'éloigne pour jouer, puis revient. Ce mouvement aller-retour est fondateur.",
    conclusion: "👉 « Je peux partir, explorer, mais je reste relié. »",
    narration: "Selon Bowlby, l'enfant a besoin d'une base sécurisante pour explorer le monde. L'adulte stable lui permet ce mouvement d'aller-retour entre exploration et retour."
  },
  // SLIDE 15 - Estime de soi
  {
    id: 15,
    type: 'selfesteem',
    title: "Le jeu et l'estime de soi",
    color: "#10B981",
    intro: "Lorsque l'enfant réussit :",
    achievements: [
      { icon: "🏗️", text: "À empiler" },
      { icon: "💧", text: "À transvaser" },
      { icon: "🧱", text: "À construire" },
      { icon: "🦘", text: "À sauter" },
      { icon: "🤝", text: "À coopérer" }
    ],
    result: "Il intègre : « Je suis capable. »",
    conclusion: "Le jeu devient alors un espace de valorisation. Et cette compétence perçue nourrit l'estime de soi.",
    narration: "Chaque réussite dans le jeu renforce le sentiment de compétence de l'enfant et nourrit son estime de soi."
  },
  // SLIDE 16 - Rôle AP
  {
    id: 16,
    type: 'aprole',
    title: "Le rôle de l'auxiliaire de puériculture",
    color: "#10B981",
    warning: "L'AP ne « fait pas jouer ».",
    intro: "Elle :",
    actions: [
      { icon: "🏠", text: "Aménage un espace riche et sécurisant" },
      { icon: "👁️", text: "Observe sans envahir" },
      { icon: "💬", text: "Met des mots sur les actions" },
      { icon: "🤲", text: "Soutient sans faire à la place" },
      { icon: "⭐", text: "Valorise sans surévaluer" }
    ],
    conclusion: "Elle permet à l'enfant de devenir acteur de son développement.",
    narration: "L'AP ne fait pas jouer l'enfant. Elle aménage l'espace, observe, met des mots, soutient et valorise pour permettre à l'enfant de devenir acteur."
  },
  // SLIDE 17 - Conclusion jeu
  {
    id: 17,
    type: 'conclusion',
    title: "Conclusion : Le jeu, laboratoire du « Je »",
    color: "#10B981",
    intro: "À travers le jeu, l'enfant :",
    points: [
      { icon: "🏃", text: "Découvre son corps" },
      { icon: "🌍", text: "Expérimente le monde" },
      { icon: "💪", text: "Teste ses capacités" },
      { icon: "👥", text: "Explore les relations" },
      { icon: "✋", text: "Affirme ses choix" },
      { icon: "🪪", text: "Construit son identité" }
    ],
    highlight: "Le jeu est le laboratoire du « Je ».",
    finalNote: "Ce n'est pas un temps secondaire. C'est un espace fondateur. Et accompagner le jeu, c'est accompagner la naissance du sujet.",
    narration: "Le jeu est le laboratoire du Je. Accompagner le jeu, c'est accompagner la naissance du sujet."
  },
  // SLIDE 18 - Quiz jeu
  {
    id: 18,
    type: 'quiz',
    title: "Quiz - Le jeu et l'identité",
    color: "#10B981",
    quizType: 'eveil',
    narration: "Testons vos connaissances sur le jeu et la construction de l'identité."
  },
  // SLIDE 19 - Activités encadrées section
  {
    id: 19,
    type: 'section',
    title: "Activités encadrées en petit groupe",
    subtitle: "Propositions adaptées et rôle professionnel",
    icon: Palette,
    color: "#F59E0B",
    narration: "Découvrons maintenant les activités encadrées que vous pouvez proposer en petit groupe."
  },
  // SLIDE 20 - Activités créatives
  {
    id: 20,
    type: 'activity',
    title: "Activités créatives",
    subtitle: "Peinture, collage, pâte à modeler",
    color: "#F59E0B",
    icon: "🎨",
    image: "https://customer-assets.emergentagent.com/job_7319947e-f6fb-47e1-823c-3efab0745ed3/artifacts/enx3g5rd_image.png",
    proposals: [
      "Peinture au doigt",
      "Gommettes",
      "Dessin libre",
      "Manipulation pâte à modeler"
    ],
    developments: [
      { icon: "✋", text: "Motricité fine" },
      { icon: "👁️", text: "Coordination œil-main" },
      { icon: "💡", text: "Créativité" },
      { icon: "❤️", text: "Expression émotionnelle" },
      { icon: "⭐", text: "Estime de soi" }
    ],
    role: [
      "Adapter le matériel à l'âge",
      "Expliquer les consignes simplement",
      "Valoriser les productions",
      "Observer concentration et interactions"
    ],
    narration: "Les activités créatives développent la motricité fine, la coordination, la créativité et l'estime de soi. L'AP adapte le matériel et valorise les productions."
  },
  // SLIDE 21 - Parcours motricité
  {
    id: 21,
    type: 'activity',
    title: "Parcours de motricité",
    subtitle: "Développer les compétences motrices globales",
    color: "#F59E0B",
    icon: "🏃",
    image: "https://customer-assets.emergentagent.com/job_7319947e-f6fb-47e1-823c-3efab0745ed3/artifacts/s6fszpzh_image.png",
    proposals: [
      "Tunnel",
      "Modules en mousse",
      "Sauter, ramper, grimper"
    ],
    developments: [
      { icon: "🏃", text: "Développement moteur global" },
      { icon: "🎯", text: "Coordination" },
      { icon: "🗺️", text: "Repérage spatial" },
      { icon: "💪", text: "Confiance en soi" }
    ],
    role: [
      "Sécuriser l'espace",
      "Encourager sans forcer",
      "Respecter le rythme individuel"
    ],
    narration: "Le parcours de motricité développe les compétences motrices globales, la coordination et la confiance en soi. L'AP sécurise et respecte le rythme de chacun."
  },
  // SLIDE 22 - Atelier cuisine
  {
    id: 22,
    type: 'activity',
    title: "Atelier cuisine",
    subtitle: "Découverte sensorielle et coopération",
    color: "#F59E0B",
    icon: "🍪",
    image: "https://customer-assets.emergentagent.com/job_7319947e-f6fb-47e1-823c-3efab0745ed3/artifacts/8x80bsx3_image.png",
    developments: [
      { icon: "👅", text: "Découverte sensorielle" },
      { icon: "🔄", text: "Notion de transformation" },
      { icon: "📋", text: "Respect des consignes" },
      { icon: "🤝", text: "Travail en coopération" }
    ],
    narration: "L'atelier cuisine permet la découverte sensorielle, la compréhension de la transformation et le travail en coopération."
  },
  // SLIDE 23 - Comptines
  {
    id: 23,
    type: 'activity',
    title: "Comptines et jeux musicaux",
    subtitle: "Langage, mémoire et socialisation",
    color: "#F59E0B",
    icon: "🎵",
    image: "https://customer-assets.emergentagent.com/job_7319947e-f6fb-47e1-823c-3efab0745ed3/artifacts/pu8dav1k_image.png",
    developments: [
      { icon: "💬", text: "Langage" },
      { icon: "🧠", text: "Mémoire auditive" },
      { icon: "🥁", text: "Rythme" },
      { icon: "👥", text: "Socialisation" }
    ],
    narration: "Les comptines et jeux musicaux développent le langage, la mémoire auditive, le sens du rythme et la socialisation."
  },
  // SLIDE 38-45 - Transvasement
  {
    id: 24,
    type: 'transvasement_complete',
    title: "🫗 LES ACTIVITÉS DE TRANSVASEMENT",
    subtitle: "À intégrer dans les activités encadrées ou en jeu libre accompagné",
    image: "https://customer-assets.emergentagent.com/job_19934833-ba36-40ce-9688-bc430ef10bd5/artifacts/iyi58miw_image.png",
    color: "#8B5CF6",
    definition: {
      text: "Les activités de transvasement consistent à transférer une matière d'un contenant à un autre :",
      materials: ["Eau", "Riz", "Semoule", "Lentilles", "Sable", "Perles (selon âge)", "Petits objets sécurisés"],
      note: "Elles peuvent être proposées en activité encadrée ou en coin autonome aménagé."
    },
    interests: [
      {
        number: "1️⃣",
        title: "Motricité fine",
        points: ["Coordination main-œil", "Précision du geste", "Contrôle du poignet", "Stabilisation du bras"],
        note: "👉 Prépare à l'écriture."
      },
      {
        number: "2️⃣",
        title: "Concentration",
        points: ["Activité répétitive et structurante", "Apaisante", "Favorise l'attention soutenue"]
      },
      {
        number: "3️⃣",
        title: "Structuration cognitive",
        points: ["Notion de plein / vide", "Quantité", "Cause / effet", "Comparaison", "Permanence de l'objet (chez le plus jeune)"]
      },
      {
        number: "4️⃣",
        title: "Autonomie",
        points: ["L'enfant agit seul", "Expérimente", "Corrige ses erreurs"],
        note: "👉 Développe confiance et estime de soi."
      },
      {
        number: "5️⃣",
        title: "Régulation émotionnelle",
        intro: "Les activités de transvasement ont souvent un effet :",
        points: ["Apaisant", "Structurant", "Contenant"],
        useful: ["Enfant agité", "Enfant anxieux", "Difficulté de concentration"]
      }
    ],
    ageAdaptation: [
      {
        age: "10–18 mois",
        emoji: "🍼",
        material: ["Grosses graines", "Gobelets larges", "Cuillères épaisses"],
        objective: ["Exploration sensorielle", "Découverte matière", "Coordination simple"]
      },
      {
        age: "2–3 ans",
        emoji: "👦",
        material: ["Petits contenants", "Entonnoirs", "Cuillères adaptées"],
        objective: ["Précision du geste", "Ajustement de la force", "Notion quantité"]
      },
      {
        age: "3–4 ans",
        emoji: "👧",
        material: ["Pipettes", "Petits bols gradués", "Tamis"],
        objective: ["Contrôle fin", "Coordination bilatérale", "Pré-mathématiques"]
      }
    ],
    roleAP: {
      actions: ["Installer un espace sécurisé", "Adapter le matériel à l'âge", "Vérifier absence risque ingestion", "Expliquer consigne simple", "Observer sans surdiriger", "Mettre des mots sur l'action"],
      example: "« Tu remplis le verre… maintenant il est plein. »"
    },
    amenagement: {
      items: ["Bac sensoriel", "Tapis sous l'activité", "Matériel accessible", "Coin calme"],
      important: "L'activité doit rester expérimentale et non performante."
    },
    narration: "Les activités de transvasement développent la motricité fine, la concentration, la structuration cognitive, l'autonomie et la régulation émotionnelle. L'AP adapte le matériel selon l'âge et observe sans surdiriger."
  },
  // SLIDE 45-46 - Attentes jury
  {
    id: 25,
    type: 'section',
    title: "Ce que le jury attend",
    subtitle: "Les critères d'évaluation",
    icon: Award,
    color: "#F97316",
    narration: "Voyons maintenant ce que le jury attend de vous pour l'Activité 1 bis."
  },
  {
    id: 26,
    type: 'jury',
    title: "Critères d'évaluation du jury",
    color: "#F97316",
    intro: "Vous devez montrer :",
    criteria: [
      { icon: "✔", text: "Que l'activité est adaptée à l'âge" },
      { icon: "✔", text: "Que vous connaissez les apports développementaux" },
      { icon: "✔", text: "Que vous analysez vos actions" },
      { icon: "✔", text: "Que vous favorisez autonomie et socialisation" },
      { icon: "✔", text: "Que vous respectez la sécurité affective" },
      { icon: "✔", text: "Que vous adoptez une posture professionnelle adaptée" }
    ],
    narration: "Le jury attend que vous montriez l'adaptation à l'âge, la connaissance des apports développementaux, l'analyse de vos actions et une posture professionnelle adaptée."
  },
  // SLIDE 27 - Rituels et regroupement
  {
    id: 27,
    type: 'section',
    title: "Temps rituels et regroupement",
    subtitle: "Structurer le temps pour sécuriser",
    icon: Clock,
    color: "#EC4899",
    narration: "Les rituels sont essentiels pour structurer le temps et donner des repères sécurisants à l'enfant."
  },
  {
    id: 28,
    type: 'rituals',
    title: "Exemples de rituels",
    color: "#EC4899",
    rituals: [
      { icon: "🎵", text: "Chanson du bonjour" },
      { icon: "📢", text: "Appel des prénoms" },
      { icon: "📅", text: "Calendrier" },
      { icon: "📖", text: "Lecture collective" },
      { icon: "😴", text: "Rituel avant la sieste" }
    ],
    whyImportant: [
      "Structurent le temps",
      "Donnent des repères sécurisants",
      "Favorisent la cohésion du groupe",
      "Aident à la gestion des transitions"
    ],
    theory: "Les rituels répondent au besoin de sécurité affective (Maslow) et favorisent l'organisation psychique de l'enfant.",
    narration: "Les rituels comme la chanson du bonjour ou l'appel des prénoms structurent le temps et répondent au besoin de sécurité affective de l'enfant."
  },
  // SLIDE 26-29 - Jeu libre
  {
    id: 29,
    type: 'section',
    title: "Le jeu libre",
    subtitle: "Aménagement et accompagnement",
    icon: Play,
    color: "#6366F1",
    narration: "Le jeu libre est un temps essentiel qui nécessite un aménagement réfléchi et une posture professionnelle adaptée."
  },
  // SLIDES 27-48 - AMÉNAGEMENT DE L'ESPACE
  {
    id: 30,
    type: 'section',
    title: "L'aménagement de l'espace",
    subtitle: "Adaptés à l'âge et aux normes françaises",
    icon: Home,
    color: "#8B5CF6",
    narration: "L'aménagement de l'espace est fondamental en EAJE. Il influence le développement, la sécurité et le comportement des enfants."
  },
  {
    id: 31,
    type: 'spaces',
    title: "Aménagement de l'espace",
    color: "#6366F1",
    image: "https://customer-assets.emergentagent.com/job_7319947e-f6fb-47e1-823c-3efab0745ed3/artifacts/vjlvoy2i_image.png",
    spaces: [
      { icon: "🍳", text: "Coin cuisine" },
      { icon: "🧱", text: "Coin construction" },
      { icon: "📚", text: "Coin lecture" },
      { icon: "👶", text: "Coin poupées" },
      { icon: "🏃", text: "Coin motricité" },
      { icon: "🍼", text: "Espace bébé sécurisé" }
    ],
    principles: [
      "Matériel accessible",
      "Sécurité garantie",
      "Zones bien définies",
      "Espaces calmes / dynamiques distincts"
    ],
    narration: "L'espace de jeu libre doit proposer différents coins avec du matériel accessible, une sécurité garantie et des zones bien définies."
  },
  {
    id: 32,
    type: 'fundamentals',
    title: "Pourquoi l'aménagement est fondamental ?",
    color: "#8B5CF6",
    intro: "L'espace en EAJE n'est pas neutre. Il influence :",
    influences: [
      { icon: "🏃", text: "Le développement moteur" },
      { icon: "🎯", text: "L'autonomie" },
      { icon: "👥", text: "La socialisation" },
      { icon: "❤️", text: "La sécurité affective" },
      { icon: "🛡️", text: "La prévention des risques" },
      { icon: "😊", text: "Le comportement des enfants" }
    ],
    conclusion: "Un espace bien pensé devient un outil pédagogique invisible.",
    narration: "L'espace en EAJE influence le développement moteur, l'autonomie, la socialisation, la sécurité affective et la prévention des risques."
  },
  {
    id: 33,
    type: 'dev_support',
    title: "L'aménagement comme support du développement",
    subtitle: "Développement moteur",
    color: "#10B981",
    icon: "🏃",
    intro: "Un espace adapté permet :",
    points: [
      "Motricité libre (Pikler)",
      "Exploration spontanée",
      "Acquisition des postures sans contrainte",
      "Coordination globale"
    ],
    examples: [
      { age: "Bébés", item: "Tapis ferme" },
      { age: "Marcheurs", item: "Modules mousse" },
      { age: "Moyens/Grands", item: "Parcours motricité" }
    ],
    narration: "Un espace adapté permet la motricité libre selon Pikler, l'exploration spontanée et l'acquisition des postures."
  },
  {
    id: 34,
    type: 'dev_support',
    title: "L'aménagement comme support du développement",
    subtitle: "Développement cognitif",
    color: "#3B82F6",
    icon: "🧠",
    intro: "L'environnement stimule :",
    points: [
      "Curiosité",
      "Expérimentation",
      "Résolution de problèmes",
      "Permanence de l'objet",
      "Construction logique"
    ],
    narration: "L'environnement stimule la curiosité, l'expérimentation et la résolution de problèmes."
  },
  {
    id: 35,
    type: 'dev_support',
    title: "L'aménagement comme support du développement",
    subtitle: "Développement affectif",
    color: "#EC4899",
    icon: "❤️",
    intro: "Un espace structuré rassure :",
    points: [
      "Repères stables",
      "Zones identifiées",
      "Coins cocooning",
      "Espaces calmes"
    ],
    theory: "Répond au besoin de sécurité (Maslow)",
    narration: "Un espace structuré avec des repères stables et des coins cocooning répond au besoin de sécurité selon Maslow."
  },
  {
    id: 36,
    type: 'dev_support',
    title: "L'aménagement comme support du développement",
    subtitle: "Développement social",
    color: "#F59E0B",
    icon: "👥",
    intro: "Les coins symboliques favorisent :",
    symbolics: [
      { icon: "🍳", item: "Coin cuisine" },
      { icon: "👶", item: "Coin poupées" },
      { icon: "🚗", item: "Coin garage" }
    ],
    points: [
      "Jeu d'imitation",
      "Interaction",
      "Coopération",
      "Gestion des conflits"
    ],
    narration: "Les coins symboliques comme la cuisine ou les poupées favorisent le jeu d'imitation et la coopération."
  },
  {
    id: 37,
    type: 'age_adaptation',
    title: "Adaptation selon l'âge : 0-12 mois",
    color: "#EC4899",
    icon: "🍼",
    needs: [
      "Motricité libre",
      "Exploration sensorielle",
      "Sécurité maximale"
    ],
    setup: [
      { icon: "🟫", text: "Tapis ferme" },
      { icon: "🪞", text: "Miroirs sécurisés" },
      { icon: "🎵", text: "Hochets adaptés" },
      { icon: "🧸", text: "Objets légers, non dangereux" }
    ],
    warning: "Pas de mise en position forcée",
    narration: "Pour les 0-12 mois : motricité libre, exploration sensorielle et sécurité maximale. Pas de mise en position forcée."
  },
  {
    id: 38,
    type: 'age_adaptation',
    title: "Adaptation selon l'âge : 1-2 ans",
    color: "#10B981",
    icon: "👦",
    needs: [
      "Déplacement",
      "Manipulation",
      "Imitation"
    ],
    setup: [
      { icon: "🧱", text: "Modules bas" },
      { icon: "🫗", text: "Bacs de transvasement" },
      { icon: "🔲", text: "Jouets à encastrement" },
      { icon: "📚", text: "Coin lecture accessible" }
    ],
    narration: "Pour les 1-2 ans : déplacement, manipulation et imitation avec des modules bas et jouets à encastrement."
  },
  {
    id: 39,
    type: 'age_adaptation',
    title: "Adaptation selon l'âge : 2-3 ans",
    color: "#F59E0B",
    icon: "👧",
    needs: [
      "Jeu symbolique",
      "Motricité fine",
      "Interaction sociale"
    ],
    setup: [
      { icon: "🍳", text: "Coin cuisine" },
      { icon: "👗", text: "Coin déguisement" },
      { icon: "🧱", text: "Jeux de construction" },
      { icon: "🎨", text: "Matériel créatif" }
    ],
    narration: "Pour les 2-3 ans : jeu symbolique, motricité fine et interaction sociale avec coins symboliques et matériel créatif."
  },
  {
    id: 40,
    type: 'section',
    title: "Respect des normes françaises",
    subtitle: "Sécurité, réglementation et prévention",
    icon: Shield,
    color: "#EF4444",
    narration: "L'aménagement doit impérativement respecter les normes françaises de sécurité."
  },
  {
    id: 41,
    type: 'norms',
    title: "Normes de sécurité",
    color: "#EF4444",
    icon: "🛡️",
    norms: [
      { icon: "✓", text: "Norme CE" },
      { icon: "✓", text: "Norme NF EN 71 (sécurité jouets)" },
      { icon: "⚠️", text: "Pas de petites pièces < 3 ans" },
      { icon: "🧪", text: "Matériel non toxique" },
      { icon: "⭕", text: "Coins arrondis" },
      { icon: "🏗️", text: "Stabilité mobilier" }
    ],
    narration: "Les normes incluent CE, NF EN 71, absence de petites pièces, matériel non toxique et coins arrondis."
  },
  {
    id: 42,
    type: 'norms',
    title: "Réglementation EAJE",
    color: "#3B82F6",
    icon: "📋",
    norms: [
      { icon: "📄", text: "Décret relatif aux établissements d'accueil" },
      { icon: "📐", text: "Surface minimale par enfant" },
      { icon: "♿", text: "Accessibilité" },
      { icon: "🧹", text: "Hygiène" },
      { icon: "🔧", text: "Entretien régulier" }
    ],
    narration: "La réglementation EAJE impose surface minimale, accessibilité, hygiène et entretien régulier."
  },
  {
    id: 43,
    type: 'norms',
    title: "Prévention des risques",
    color: "#F59E0B",
    icon: "⚠️",
    norms: [
      { icon: "🚫", text: "Pas d'objets dangereux à portée" },
      { icon: "👶", text: "Matériel adapté à l'âge" },
      { icon: "👁️", text: "Surveillance permanente" },
      { icon: "🔌", text: "Sécurisation prises électriques" }
    ],
    narration: "La prévention implique d'éviter les objets dangereux, adapter le matériel et assurer une surveillance permanente."
  },
  {
    id: 44,
    type: 'quality_games',
    title: "Qualité pédagogique des jeux",
    color: "#8B5CF6",
    intro: "Un jeu adapté doit :",
    criteria: [
      { icon: "✔", text: "Correspondre aux capacités motrices" },
      { icon: "✔", text: "Être accessible en autonomie" },
      { icon: "✔", text: "Favoriser la manipulation" },
      { icon: "✔", text: "Stimuler sans surstimuler" },
      { icon: "✔", text: "Être varié mais pas surabondant" }
    ],
    warning: "Trop de jeux = dispersion et agitation",
    narration: "Un jeu adapté doit correspondre aux capacités, être accessible et stimuler sans surstimuler."
  },
  {
    id: 45,
    type: 'prevention_tool',
    title: "L'espace comme outil de prévention",
    color: "#10B981",
    reduces: [
      { icon: "⬇️", text: "Les chutes" },
      { icon: "⬇️", text: "Les conflits" },
      { icon: "⬇️", text: "L'agitation" },
      { icon: "⬇️", text: "La frustration" },
      { icon: "⬇️", text: "Les risques d'ingestion" }
    ],
    promotes: [
      { icon: "⬆️", text: "L'autonomie" },
      { icon: "⬆️", text: "L'apaisement" },
      { icon: "⬆️", text: "La concentration" }
    ],
    narration: "Un bon aménagement réduit chutes, conflits et agitation, tout en favorisant autonomie et concentration."
  },
  {
    id: 46,
    type: 'ap_amenagement',
    title: "Rôle de l'AP dans l'aménagement",
    color: "#EC4899",
    intro: "L'auxiliaire de puériculture :",
    actions: [
      { icon: "👁️", text: "Observe les besoins du groupe" },
      { icon: "🔄", text: "Ajuste les espaces" },
      { icon: "✓", text: "Vérifie la conformité du matériel" },
      { icon: "🎯", text: "Anticipe les risques" },
      { icon: "👥", text: "Participe aux réunions d'équipe" },
      { icon: "💡", text: "Propose des améliorations" }
    ],
    conclusion: "Elle est actrice de l'environnement éducatif.",
    narration: "L'AP observe, ajuste, vérifie la conformité, anticipe les risques et propose des améliorations."
  },
  {
    id: 47,
    type: 'identity_link',
    title: "Lien avec la construction du « Je »",
    color: "#6366F1",
    intro: "Un espace adapté permet à l'enfant :",
    points: [
      { icon: "🎯", text: "De choisir" },
      { icon: "💪", text: "D'agir seul" },
      { icon: "⭐", text: "De réussir" },
      { icon: "🚶", text: "De se déplacer librement" },
      { icon: "🔬", text: "D'expérimenter" }
    ],
    conclusion: "L'espace soutient la construction identitaire.",
    narration: "Un espace adapté permet de choisir, d'agir seul et d'expérimenter. L'espace soutient la construction identitaire."
  },
  {
    id: 48,
    type: 'jury_amenagement',
    title: "Ce que le jury attend - Aménagement",
    color: "#EF4444",
    intro: "Dans le livret, il faut montrer :",
    expectations: [
      { icon: "✔", text: "Que vous adaptez l'espace à l'âge" },
      { icon: "✔", text: "Que vous connaissez les normes de sécurité" },
      { icon: "✔", text: "Que vous identifiez les risques" },
      { icon: "✔", text: "Que vous faites le lien développement / aménagement" },
      { icon: "✔", text: "Que vous participez à la réflexion d'équipe" }
    ],
    narration: "Le jury attend que vous montriez votre capacité à adapter l'espace et connaître les normes."
  },
  {
    id: 49,
    type: 'vae_example',
    title: "Exemple de formulation VAE",
    color: "#10B981",
    wrong: "On installe les jeux.",
    correct: "J'aménage l'espace en fonction des capacités motrices du groupe afin de favoriser l'autonomie tout en prévenant les risques de chute. Je veille à la conformité du matériel aux normes en vigueur.",
    narration: "Voici comment transformer une formulation simple en une formulation professionnelle."
  },
  {
    id: 50,
    type: 'quiz_amenagement',
    title: "Quiz : Aménagement de l'espace",
    color: "#8B5CF6",
    question: "Quel aménagement est adapté pour un enfant de 0-12 mois ?",
    options: [
      { id: 'a', text: "Un parcours motricité avec obstacles", correct: false },
      { id: 'b', text: "Un tapis ferme avec miroir sécurisé", correct: true },
      { id: 'c', text: "Un coin déguisement", correct: false },
      { id: 'd', text: "Des jeux de construction complexes", correct: false }
    ],
    explanation: "Pour les 0-12 mois, on privilégie le tapis ferme pour la motricité libre et les miroirs sécurisés.",
    narration: "Question : Quel aménagement est adapté pour un enfant de 0-12 mois ?"
  },
  {
    id: 51,
    type: 'quiz_amenagement',
    title: "Quiz : Normes de sécurité",
    color: "#EF4444",
    question: "Quelle norme garantit la sécurité des jouets en France ?",
    options: [
      { id: 'a', text: "Norme ISO 9001", correct: false },
      { id: 'b', text: "Norme NF EN 71", correct: true },
      { id: 'c', text: "Norme AFNOR 2000", correct: false },
      { id: 'd', text: "Norme CE uniquement suffit", correct: false }
    ],
    explanation: "La norme NF EN 71 est la norme européenne de sécurité des jouets, complémentaire au marquage CE.",
    narration: "Question : Quelle norme garantit la sécurité des jouets en France ?"
  },
  // SLIDES 49-60 - LE JEU LIBRE (contenu enrichi)
  {
    id: 52,
    type: 'section',
    title: "Le Jeu Libre",
    subtitle: "Ce qu'il développe et le rôle de l'auxiliaire de puériculture",
    icon: Play,
    color: "#6366F1",
    narration: "Le jeu libre est fondamental pour le développement de l'enfant. Découvrons ce qu'il apporte et le rôle de l'AP."
  },
  {
    id: 53,
    type: 'jeu_libre_definition',
    title: "Définition du jeu libre",
    color: "#6366F1",
    intro: "Le jeu libre est un temps durant lequel :",
    points: [
      { icon: "🎯", text: "L'enfant choisit son activité" },
      { icon: "👥", text: "L'enfant décide avec qui il joue" },
      { icon: "⏱️", text: "L'enfant détermine la durée" },
      { icon: "🔄", text: "L'enfant organise son action" }
    ],
    emphasis: "Sans consigne imposée par l'adulte.",
    conclusion: "L'adulte est présent, mais non directeur.",
    narration: "Le jeu libre permet à l'enfant de choisir son activité, ses partenaires, la durée et d'organiser son action sans consigne imposée."
  },
  {
    id: 54,
    type: 'jeu_libre_dev',
    title: "Développement moteur par le jeu libre",
    color: "#10B981",
    icon: "🏃",
    intro: "Selon l'âge, le jeu libre développe :",
    points: [
      { icon: "🚶", text: "Motricité globale (ramper, grimper, courir)" },
      { icon: "🎯", text: "Coordination" },
      { icon: "⚖️", text: "Équilibre" },
      { icon: "✋", text: "Motricité fine (empiler, encastrer, transvaser)" }
    ],
    conclusion: "L'enfant ajuste ses mouvements par l'expérimentation.",
    narration: "Le jeu libre développe la motricité globale et fine. L'enfant ajuste ses mouvements par l'expérimentation."
  },
  {
    id: 55,
    type: 'jeu_libre_dev',
    title: "Développement cognitif par le jeu libre",
    color: "#3B82F6",
    icon: "🧠",
    intro: "Le jeu libre permet :",
    points: [
      { icon: "🧩", text: "Résolution de problèmes" },
      { icon: "🔢", text: "Logique" },
      { icon: "🔄", text: "Cause / effet" },
      { icon: "🎯", text: "Anticipation" },
      { icon: "💡", text: "Créativité et imagination" }
    ],
    theory: "Selon Piaget, l'enfant construit ses connaissances par l'action.",
    narration: "Le jeu libre développe la résolution de problèmes, la logique et la créativité. Selon Piaget, l'enfant apprend par l'action."
  },
  {
    id: 56,
    type: 'jeu_libre_dev',
    title: "Développement émotionnel par le jeu libre",
    color: "#EC4899",
    icon: "❤️",
    intro: "Le jeu libre aide l'enfant à :",
    points: [
      { icon: "😤", text: "Gérer la frustration" },
      { icon: "🎭", text: "Réguler ses émotions" },
      { icon: "🔁", text: "Rejouer des situations vécues" },
      { icon: "😨", text: "Exprimer ses peurs" },
      { icon: "⭐", text: "Construire son estime de soi" }
    ],
    highlight: "Quand il réussit, il intègre : « Je suis capable. »",
    narration: "Le jeu libre aide l'enfant à gérer ses émotions et construire son estime de soi. Quand il réussit, il intègre 'Je suis capable'."
  },
  {
    id: 57,
    type: 'jeu_libre_dev',
    title: "Construction du « Je » par le jeu libre",
    color: "#8B5CF6",
    icon: "🪪",
    intro: "À travers le jeu libre, l'enfant :",
    points: [
      { icon: "🎯", text: "Choisit" },
      { icon: "✅", text: "Décide" },
      { icon: "🔬", text: "Expérimente" },
      { icon: "❤️", text: "Affirme ses préférences" },
      { icon: "🚫", text: "S'oppose" },
      { icon: "🤝", text: "Coopère" }
    ],
    conclusion: "Il devient sujet. Le jeu libre participe à la construction identitaire.",
    narration: "Le jeu libre permet à l'enfant de choisir, décider, expérimenter. Il devient sujet et construit son identité."
  },
  {
    id: 58,
    type: 'jeu_libre_dev',
    title: "Développement social et autonomie",
    color: "#F59E0B",
    icon: "👥",
    intro: "Le jeu libre favorise :",
    socialPoints: [
      { icon: "💬", text: "Les interactions spontanées" },
      { icon: "🤲", text: "Le partage" },
      { icon: "🤝", text: "La négociation" },
      { icon: "⚖️", text: "La gestion des conflits" },
      { icon: "📜", text: "L'apprentissage des règles sociales" }
    ],
    autonomyPoints: [
      { icon: "📋", text: "S'organise" },
      { icon: "🧹", text: "Range" },
      { icon: "✋", text: "Manipule" },
      { icon: "🔍", text: "Explore sans dépendance constante" }
    ],
    conclusion: "Il prépare à la vie collective et développe confiance et initiative.",
    narration: "Le jeu libre favorise les interactions sociales, le partage et l'autonomie. Il prépare à la vie collective."
  },
  {
    id: 59,
    type: 'section',
    title: "Rôle de l'AP dans le jeu libre",
    subtitle: "Ni absente, ni intrusive : une posture d'accompagnement",
    icon: Users,
    color: "#EC4899",
    narration: "L'auxiliaire de puériculture n'est ni absente, ni intrusive. Elle adopte une posture d'accompagnement."
  },
  {
    id: 60,
    type: 'ap_jeu_libre',
    title: "Les missions de l'AP dans le jeu libre",
    color: "#EC4899",
    missions: [
      { 
        title: "Garantir la sécurité",
        icon: "🛡️",
        points: ["Surveiller discrètement", "Anticiper les risques", "Adapter l'espace"]
      },
      { 
        title: "Observer",
        icon: "👁️",
        points: ["Les compétences émergentes", "Les interactions", "Les difficultés", "Les centres d'intérêt"]
      },
      { 
        title: "Mettre des mots",
        icon: "💬",
        points: ["« Tu as réussi à empiler »", "« Tu sembles en colère »", "« Tu partages avec ton camarade »"]
      }
    ],
    note: "L'observation nourrit le projet pédagogique.",
    narration: "L'AP garantit la sécurité, observe les compétences et difficultés, et met des mots sur ce que vit l'enfant."
  },
  {
    id: 61,
    type: 'ap_jeu_libre_suite',
    title: "Les missions de l'AP (suite)",
    color: "#EC4899",
    missions: [
      { 
        title: "Réguler les conflits",
        icon: "⚖️",
        points: ["Sans juger", "Rappeler la règle", "Aider à exprimer les émotions", "Proposer une solution"]
      },
      { 
        title: "Soutenir sans faire à la place",
        icon: "🤲",
        avoid: ["Intervenir trop vite", "Corriger immédiatement", "Diriger le jeu"],
        do: "Elle laisse l'enfant expérimenter."
      },
      { 
        title: "Enrichir si nécessaire",
        icon: "✨",
        points: ["Proposer un nouvel objet", "Introduire une idée", "Stimuler l'imaginaire"],
        note: "Toujours en respectant l'initiative de l'enfant."
      }
    ],
    narration: "L'AP régule les conflits sans juger, soutient sans faire à la place, et enrichit si nécessaire en respectant l'initiative de l'enfant."
  },
  {
    id: 62,
    type: 'equilibre_jeu_libre',
    title: "L'équilibre à trouver",
    color: "#F59E0B",
    tooMuch: {
      title: "Trop d'intervention",
      icon: "⚠️",
      effects: ["Freine l'autonomie", "Diminue la créativité", "Rend l'enfant dépendant"]
    },
    notEnough: {
      title: "Pas assez d'intervention",
      icon: "⚠️",
      effects: ["Risque d'insécurité", "Conflits non régulés", "Isolement"]
    },
    conclusion: "La posture professionnelle est un ajustement permanent.",
    narration: "Trop d'intervention freine l'autonomie, pas assez crée de l'insécurité. La posture professionnelle est un ajustement permanent."
  },
  {
    id: 63,
    type: 'references_jeu_libre',
    title: "Références théoriques - Jeu libre",
    color: "#3B82F6",
    references: [
      { author: "Pikler", concept: "Motricité libre" },
      { author: "Piaget", concept: "Apprentissage par l'action" },
      { author: "Bowlby", concept: "Base sécurisante" },
      { author: "Maslow", concept: "Besoin d'autonomie et d'estime" },
      { author: "Henderson", concept: "Besoin de se divertir et d'apprendre" }
    ],
    narration: "Les références théoriques incluent Pikler pour la motricité libre, Piaget pour l'apprentissage par l'action, Bowlby pour la base sécurisante."
  },
  // SLIDE 61+ - Éveil bébés (anciens slides 51+)
  {
    id: 64,
    type: 'section',
    title: "L'éveil chez les bébés",
    subtitle: "Accompagner le développement du tout-petit",
    icon: Baby,
    color: "#14B8A6",
    narration: "L'éveil des bébés nécessite une attention particulière et des propositions adaptées à chaque étape de développement."
  },
  {
    id: 65,
    type: 'baby_dev_complete',
    title: "0-3 mois",
    subtitle: "Le temps des premières découvertes sensorielles",
    color: "#14B8A6",
    image: "https://customer-assets.emergentagent.com/job_7319947e-f6fb-47e1-823c-3efab0745ed3/artifacts/ziyo2rqo_image.png",
    development: {
      moteur: [
        "Réflexes archaïques (Moro, grasping…)",
        "Motricité non volontaire",
        "Tonicité en flexion",
        "Contrôle progressif de la tête"
      ],
      sensoriel: [
        "Vision floue mais sensible aux contrastes",
        "Reconnaissance voix parentale",
        "Sensibilité au toucher"
      ],
      relationnel: [
        "Sourire réflexe puis social",
        "Recherche du regard",
        "Apaisement par la voix"
      ]
    },
    activities: [
      "Parler doucement",
      "Chanter des comptines simples",
      "Regard face à face",
      "Tapis au sol ferme",
      "Mobile contrasté (noir/blanc)",
      "Temps sur le ventre courts et sécurisés",
      "Touchers enveloppants"
    ],
    posture: [
      "Rythme lent",
      "Respect des temps d'éveil courts",
      "Observation des signes de fatigue",
      "Interaction individualisée"
    ],
    narration: "De 0 à 3 mois, le bébé développe ses réflexes, sa vision contrastée et son lien relationnel. L'AP propose un éveil doux et individualisé."
  },
  {
    id: 66,
    type: 'baby_dev_complete_v2',
    title: "3-6 mois",
    subtitle: "Le temps de la découverte active",
    color: "#14B8A6",
    image: "https://customer-assets.emergentagent.com/job_7319947e-f6fb-47e1-823c-3efab0745ed3/artifacts/nugbco6i_image.png",
    development: {
      moteur: [
        "Se retourne",
        "Tient sa tête",
        "Début préhension volontaire"
      ],
      sensoriel: [
        "Porte à la bouche",
        "Manipule",
        "Explore textures"
      ],
      cognitif: [
        "Cause / effet simple",
        "Intérêt pour répétition"
      ]
    },
    activities: [
      "Tapis d'éveil",
      "Hochets légers",
      "Jeux sonores",
      "Objets de textures variées",
      "Jeux de miroir",
      "Comptines gestuelles"
    ],
    toAvoid: [
      "Mise en position assise forcée",
      "Stimulation excessive",
      "Trop de jouets simultanément"
    ],
    narration: "De 3 à 6 mois, le bébé se retourne, explore activement avec la bouche et découvre cause et effet. L'AP propose hochets, textures variées et comptines gestuelles."
  },
  {
    id: 67,
    type: 'baby_dev_complete_v3',
    title: "6-12 mois",
    subtitle: "Le temps de l'exploration et de l'autonomie naissante",
    color: "#14B8A6",
    image: "https://customer-assets.emergentagent.com/job_7319947e-f6fb-47e1-823c-3efab0745ed3/artifacts/c6wj485c_image.png",
    development: {
      moteur: [
        "Assise autonome",
        "Ramper / quatre pattes",
        "Se mettre debout",
        "Premiers pas"
      ],
      cognitif: [
        "Permanence de l'objet (vers 8-9 mois)",
        "Compréhension simple",
        "Imitation"
      ],
      langage: [
        "Babillage",
        "Réaction à son prénom"
      ]
    },
    activities: [
      "Parcours moteur sécurisé",
      "Jeux d'encastrement",
      "Boîtes à ouvrir/fermer",
      "Jeux de cache-cache",
      "Transvasement adapté",
      "Lecture d'images",
      "Chansons répétitives"
    ],
    domains: [
      { icon: "🏃", text: "Motricité globale et fine" },
      { icon: "👁️", text: "Coordination œil-main" },
      { icon: "🎯", text: "Attention" },
      { icon: "🧠", text: "Mémoire" },
      { icon: "❤️", text: "Relation" },
      { icon: "⭐", text: "Estime de soi" }
    ],
    narration: "De 6 à 12 mois, le bébé s'assoit, rampe, comprend la permanence de l'objet et babille. L'AP propose parcours moteur, encastrements et cache-cache."
  },
  {
    id: 68,
    type: 'baby_security',
    title: "Sécurité affective du bébé",
    subtitle: "Un bébé ne peut explorer que s'il se sent en sécurité.",
    color: "#14B8A6",
    adultRole: "L'adulte est :",
    points: [
      { icon: "🏠", text: "Une base stable" },
      { icon: "🧭", text: "Un repère rassurant" },
      { icon: "💬", text: "Un soutien verbal et émotionnel" }
    ],
    narration: "Un bébé ne peut explorer que s'il se sent en sécurité. L'adulte est une base stable, un repère rassurant, et un soutien verbal et émotionnel."
  },
  // SLIDE 36-37 - Vie sociale
  {
    id: 69,
    type: 'section',
    title: "Accompagnement à la vie sociale",
    subtitle: "Développer les compétences sociales",
    icon: Users,
    color: "#EF4444",
    narration: "L'accompagnement à la vie sociale permet à l'enfant de développer des compétences essentielles pour vivre en groupe."
  },
  {
    id: 70,
    type: 'social_skills_complete',
    title: "Compétences sociales à développer",
    color: "#EF4444",
    definition: "L'ensemble des capacités qui permettent à l'enfant d'entrer en relation avec les autres, de coopérer, de communiquer et de respecter les règles du vivre-ensemble.",
    notInnate: "Elles ne sont pas innées : Elles s'apprennent progressivement.",
    skills: [
      {
        number: "1️⃣",
        title: "La communication",
        toDevelop: ["Regarder l'autre", "Répondre à une sollicitation", "Verbaliser ses besoins", "Écouter"],
        roleAP: ["Mettre des mots sur les émotions", "Encourager l'expression verbale", "Modéliser un langage respectueux"]
      },
      {
        number: "2️⃣",
        title: "Le partage",
        intro: "Avant 3 ans, le partage n'est pas acquis. L'enfant est encore centré sur lui-même (égocentrisme selon Piaget).",
        objective: "Apprendre à attendre, comprendre la notion d'alternance",
        accompagnement: ["Introduire la notion de tour de rôle", "Utiliser le minuteur visuel", "Rassurer l'enfant"]
      },
      {
        number: "3️⃣",
        title: "La gestion des conflits",
        intro: "Les conflits sont normaux et structurants.",
        benefits: ["La négociation", "La frustration", "La résolution de problème"],
        roleAP: ["Ne pas intervenir trop vite", "Rappeler la règle", "Encourager la verbalisation", "Rester neutre"]
      },
      {
        number: "4️⃣",
        title: "L'empathie",
        intro: "Capacité à reconnaître les émotions de l'autre. Se développe progressivement.",
        moyens: ["Lire des histoires", "Mettre des mots sur les émotions", "Valoriser les comportements bienveillants"]
      },
      {
        number: "5️⃣",
        title: "Le respect des règles",
        childLearns: ["Attendre son tour", "Ranger", "Écouter une consigne", "Respecter l'espace de l'autre"],
        rulesMustBe: ["Claires", "Constantes", "Adaptées à l'âge", "Expliquées"]
      },
      {
        number: "6️⃣",
        title: "La coopération",
        activities: ["Jeu collectif", "Atelier cuisine", "Rangement collectif", "Jeux symboliques"],
        benefits: ["Sentiment d'appartenance", "Confiance en soi", "Solidarité"]
      },
      {
        number: "7️⃣",
        title: "L'autonomie sociale",
        childLearns: ["Se présenter", "Dire bonjour", "Dire merci", "S'affirmer sans agresser"]
      }
    ],
    ageEvolution: [
      { age: "0–1 an", emoji: "🍼", skills: ["Sourire social", "Interaction regard", "Imitation simple"] },
      { age: "1–2 ans", emoji: "👦", skills: ["Jeu côte à côte (jeu parallèle)", "Opposition fréquente", "Début imitation sociale"] },
      { age: "2–3 ans", emoji: "👧", skills: ["Premiers jeux coopératifs", "Conflits fréquents", "Apprentissage règles simples"] }
    ],
    roleAP: {
      emoji: "👩‍⚕️",
      title: "Rôle professionnel de l'AP",
      actions: ["Structure le cadre", "Modélise le comportement social", "Régule les interactions", "Encourage la verbalisation", "Valorise les réussites", "Favorise l'inclusion"],
      conclusion: "Elle est médiatrice sociale."
    },
    narration: "Les compétences sociales permettent à l'enfant d'entrer en relation avec les autres. Elles incluent la communication, le partage, la gestion des conflits, l'empathie, le respect des règles, la coopération et l'autonomie sociale. L'AP joue un rôle de médiatrice sociale."
  },
  // SLIDE - Synthèse sur le jeu
  {
    id: 71,
    type: 'play_synthesis',
    title: "Le jeu : un moment de plaisir et d'apprentissage",
    color: "#EC4899",
    mainMessage: "À travers le jeu, l'enfant apprend !",
    forChild: {
      title: "Pour l'enfant, le jeu c'est :",
      icon: "😊",
      points: [
        "Un moment de plaisir intense",
        "Un espace de liberté et d'expression",
        "Une source de découvertes",
        "Un temps d'émerveillement"
      ]
    },
    learnings: {
      title: "Le jeu lui permet d'apprendre à :",
      items: [
        { icon: "🤝", text: "Se socialiser avec les autres" },
        { icon: "🎁", text: "Partager" },
        { icon: "⏳", text: "Attendre son tour" },
        { icon: "🗣️", text: "Communiquer ses émotions" },
        { icon: "🧩", text: "Résoudre des problèmes" },
        { icon: "💪", text: "Gagner en autonomie" },
        { icon: "🤗", text: "Développer l'empathie" },
        { icon: "📋", text: "Respecter les règles" }
      ]
    },
    quote: "« Le jeu est le travail de l'enfant » - Maria Montessori",
    narration: "À travers le jeu, l'enfant apprend ! Pour lui, c'est un moment de plaisir intense. Le jeu lui permet de se socialiser, partager, attendre son tour, communiquer ses émotions, résoudre des problèmes et gagner en autonomie."
  },
  // SLIDE 73 - Quiz final (10 questions)
  {
    id: 72,
    type: 'quiz',
    title: "Quiz final - 10 questions",
    color: "#22C55E",
    quizType: 'final',
    narration: "Place au quiz final de 10 questions pour valider vos connaissances sur l'ensemble du cours."
  },
  // SLIDE 74 - Conclusion finale
  {
    id: 73,
    type: 'final_conclusion',
    title: "Félicitations !",
    color: "#8B5CF6",
    message: "Vous avez terminé l'Activité 1 bis du DEAP.",
    summary: [
      "Le jeu est le laboratoire du « Je »",
      "L'AP est facilitatrice, pas animatrice",
      "Adapter les activités à l'âge",
      "Observer et mettre des mots",
      "Respecter la sécurité affective"
    ],
    nextSteps: [
      "Revoir les points clés",
      "Pratiquer les activités en stage",
      "Préparer votre dossier VAE"
    ],
    narration: "Félicitations ! Vous avez terminé l'Activité 1 bis. Retenez que le jeu est le laboratoire du Je et que l'AP est facilitatrice."
  },];

const DEAPActivite1bisPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [quizState, setQuizState] = useState({
    currentQuestion: 0,
    selectedAnswer: null,
    showResult: false,
    score: 0,
    completed: false
  });

  const slide = SLIDES[currentSlide];

  const goToSlide = (slideIndex) => {
    setCurrentSlide(Math.max(0, Math.min(SLIDES.length - 1, slideIndex - 1)));
    setQuizState({ currentQuestion: 0, selectedAnswer: null, showResult: false, score: 0, completed: false });
  };

  const handlePrevious = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
    setQuizState({ currentQuestion: 0, selectedAnswer: null, showResult: false, score: 0, completed: false });
  };

  const handleNext = () => {
    setCurrentSlide((prev) => Math.min(SLIDES.length - 1, prev + 1));
    setQuizState({ currentQuestion: 0, selectedAnswer: null, showResult: false, score: 0, completed: false });
  };

  const handleSpeak = async () => {
    if (!slide.narration) return;
    setIsPlaying(true);
    
    // Transformer le texte pour une meilleure lecture TTS
    const transformTextForTTS = (text) => {
      return text
        .replace(/\bAP\b/g, "auxiliaire de puériculture")
        .replace(/\bL'AP\b/g, "L'auxiliaire de puériculture")
        .replace(/\bl'AP\b/g, "l'auxiliaire de puériculture")
        .replace(/\bDEAP\b/g, "Diplôme d'État d'Auxiliaire de Puériculture")
        .replace(/\bCAP AEPE\b/g, "CAP Accompagnant Éducatif Petite Enfance");
    };
    
    const textToSpeak = transformTextForTTS(slide.narration);
    
    try {
      const response = await fetch(`${window.location.origin}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: textToSpeak, voice: 'nova' })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.audio_base64) {
          // Convertir base64 en blob audio
          const byteCharacters = atob(data.audio_base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const audioBlob = new Blob([byteArray], { type: 'audio/mp3' });
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          audio.onended = () => setIsPlaying(false);
          audio.onerror = () => setIsPlaying(false);
          audio.play();
        } else {
          setIsPlaying(false);
        }
      } else {
        console.error('TTS response error:', response.status);
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsPlaying(false);
    }
  };

  const handleQuizAnswer = (index) => {
    if (quizState.showResult) return;
    setQuizState(prev => ({ ...prev, selectedAnswer: index }));
  };

  const handleValidateAnswer = () => {
    const quizData = slide.quizType === 'eveil' ? QUIZ_EVEIL : QUIZ_FINAL_10;
    const currentQ = quizData[quizState.currentQuestion];
    const isCorrect = quizState.selectedAnswer === currentQ.correct;
    
    setQuizState(prev => ({
      ...prev,
      showResult: true,
      score: isCorrect ? prev.score + 1 : prev.score
    }));
  };

  const handleNextQuestion = () => {
    const quizData = slide.quizType === 'eveil' ? QUIZ_EVEIL : QUIZ_FINAL_10;
    if (quizState.currentQuestion < quizData.length - 1) {
      setQuizState(prev => ({
        ...prev,
        currentQuestion: prev.currentQuestion + 1,
        selectedAnswer: null,
        showResult: false
      }));
    } else {
      setQuizState(prev => ({ ...prev, completed: true }));
    }
  };

  const renderSlideContent = () => {
    switch (slide.type) {
      case 'title':
        const TitleIcon = slide.icon;
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: `${slide.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <TitleIcon size={50} style={{ color: slide.color }} />
            </div>
            <h1 style={{ color: slide.color, fontSize: '2.5rem', marginBottom: '1rem' }}>{slide.title}</h1>
            <p style={{ color: '#666', fontSize: '1.1rem', maxWidth: '700px', margin: '0 auto', lineHeight: 1.6 }}>{slide.subtitle}</p>
          </div>
        );

      case 'table_of_contents':
        return (
          <div>
            <h2 style={{ color: slide.color, textAlign: 'center', marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ textAlign: 'center', color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>Cliquez sur une section pour y accéder directement</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', maxWidth: '900px', margin: '0 auto' }}>
              {slide.sections.map((section, index) => (
                <div
                  key={index}
                  onClick={() => goToSlide(section.targetSlide)}
                  style={{
                    padding: '1rem',
                    background: `${section.color}10`,
                    borderRadius: '0.75rem',
                    border: `2px solid ${section.color}30`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: section.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>{index + 1}</div>
                    <h4 style={{ color: section.color, margin: 0, fontSize: '0.85rem' }}>{section.title}</h4>
                  </div>
                  <p style={{ color: '#888', fontSize: '0.75rem', margin: 0 }}>Slides {section.slides}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section':
        const SectionIcon = slide.icon;
        return (
          <div style={{ 
            textAlign: 'center',
            padding: '3rem 2rem',
            borderRadius: '1.5rem',
            background: `linear-gradient(135deg, ${slide.color}15 0%, ${slide.color}30 50%, ${slide.color}15 100%)`,
            border: `3px solid ${slide.color}40`,
            boxShadow: `0 8px 32px ${slide.color}20`,
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            {SectionIcon && (
              <div style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${slide.color}, ${slide.color}CC)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                boxShadow: `0 6px 20px ${slide.color}50`
              }}>
                <SectionIcon size={50} style={{ color: 'white' }} />
              </div>
            )}
            <h2 style={{ 
              color: slide.color, 
              fontSize: '2.2rem', 
              marginBottom: '0.75rem',
              fontWeight: '700',
              textShadow: `0 2px 4px ${slide.color}20`
            }}>{slide.title}</h2>
            {slide.subtitle && (
              <p style={{ 
                color: '#555', 
                fontSize: '1.15rem',
                fontStyle: 'italic',
                maxWidth: '500px',
                margin: '0 auto',
                lineHeight: '1.6'
              }}>{slide.subtitle}</p>
            )}
          </div>
        );

      case 'section_illustrated':
        return (
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ 
              position: 'relative',
              borderRadius: '1.5rem',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
            }}>
              <img 
                src={slide.image} 
                alt={slide.title}
                style={{
                  width: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          </div>
        );

      case 'objectives':
        return (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', maxWidth: '800px', margin: '0 auto' }}>
              {slide.objectives.map((obj, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  background: `${slide.color}08`,
                  borderRadius: '0.75rem',
                  borderLeft: `4px solid ${slide.color}`
                }}>
                  <span style={{ fontSize: '1.5rem' }}>{obj.icon}</span>
                  <span style={{ color: '#333', textAlign: 'left' }}>{obj.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'theory':
        return (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#888', marginBottom: '1.5rem', fontStyle: 'italic' }}>{slide.theorist}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '500px', margin: '0 auto' }}>
              {slide.points.map((point, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  background: point.highlight ? `${slide.color}15` : '#f8f9fa',
                  borderRadius: '0.75rem',
                  border: point.highlight ? `2px solid ${slide.color}40` : 'none'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>{point.icon}</span>
                  <span style={{ color: '#333', fontWeight: point.highlight ? 600 : 400 }}>{point.text}</span>
                </div>
              ))}
            </div>
            {slide.note && <p style={{ color: '#666', marginTop: '1rem', fontSize: '0.9rem', fontStyle: 'italic' }}>{slide.note}</p>}
          </div>
        );

      case 'pyramid':
        return (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
              {slide.levels.map((level, index) => (
                <div key={index} style={{
                  width: `${60 + (slide.levels.length - index) * 8}%`,
                  padding: '0.75rem 1rem',
                  background: `${level.color}20`,
                  borderRadius: '0.5rem',
                  border: `2px solid ${level.color}`,
                  textAlign: 'center'
                }}>
                  <strong style={{ color: level.color }}>{level.level}</strong>
                  <p style={{ color: '#666', fontSize: '0.85rem', margin: '0.25rem 0 0' }}>{level.description}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'references':
        return (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem', maxWidth: '900px', margin: '0 auto' }}>
              {slide.references.map((ref, index) => (
                <div key={index} style={{
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #fff 100%)',
                  borderRadius: '1rem',
                  border: `2px solid ${slide.color}20`,
                  borderTop: `4px solid ${slide.color}`,
                  textAlign: 'left',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div>
                      <h4 style={{ color: slide.color, margin: '0 0 0.25rem', fontSize: '1.1rem' }}>{ref.author}</h4>
                      {ref.years && (
                        <span style={{ 
                          fontSize: '0.75rem', 
                          color: '#888',
                          display: 'block'
                        }}>
                          {ref.years} • {ref.nationality}
                        </span>
                      )}
                    </div>
                    {ref.profession && (
                      <span style={{
                        fontSize: '0.7rem',
                        background: `${slide.color}15`,
                        color: slide.color,
                        padding: '0.25rem 0.5rem',
                        borderRadius: '1rem',
                        fontWeight: 500,
                        whiteSpace: 'nowrap'
                      }}>
                        {ref.profession}
                      </span>
                    )}
                  </div>
                  <div style={{
                    background: `${slide.color}10`,
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    marginBottom: '0.75rem'
                  }}>
                    <p style={{ color: slide.color, fontWeight: 600, margin: '0 0 0.25rem', fontSize: '0.95rem' }}>{ref.concept}</p>
                    <p style={{ color: '#555', fontSize: '0.85rem', margin: 0, fontStyle: 'italic' }}>{ref.description}</p>
                  </div>
                  {ref.bio && (
                    <p style={{ color: '#666', fontSize: '0.8rem', margin: 0, lineHeight: 1.5 }}>
                      {ref.bio}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'concept':
        return (
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <p style={{ fontSize: '1.2rem', color: '#333', marginBottom: '1rem' }}>{slide.mainText}</p>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>{slide.subText}</p>
            <div style={{
              padding: '1.5rem',
              background: `${slide.color}10`,
              borderRadius: '1rem',
              borderLeft: `4px solid ${slide.color}`,
              marginBottom: '1rem'
            }}>
              <p style={{ color: slide.color, fontWeight: 600, fontSize: '1.1rem', margin: 0 }}>{slide.highlight}</p>
            </div>
            <p style={{ color: '#333', fontWeight: 500 }}>{slide.conclusion}</p>
          </div>
        );

      case 'content':
        return (
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '1rem' }}>{slide.title}</h2>
            {slide.intro && <p style={{ color: '#666', marginBottom: '1.5rem' }}>{slide.intro}</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {slide.points.map((point, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  background: `${slide.color}08`,
                  borderRadius: '0.75rem',
                  borderLeft: `4px solid ${slide.color}`
                }}>
                  <span style={{ fontSize: '1.5rem' }}>{point.icon}</span>
                  <span style={{ color: '#333', textAlign: 'left' }}>{point.text}</span>
                </div>
              ))}
            </div>
            {slide.highlight && (
              <div style={{
                padding: '1rem',
                background: `${slide.color}15`,
                borderRadius: '0.75rem',
                marginBottom: '1rem'
              }}>
                <p style={{ color: slide.color, fontWeight: 600, margin: 0 }}>{slide.highlight}</p>
              </div>
            )}
            {slide.conclusion && <p style={{ color: '#333', fontWeight: 500 }}>{slide.conclusion}</p>}
          </div>
        );

      case 'quiz':
        const quizData = slide.quizType === 'eveil' ? QUIZ_EVEIL : QUIZ_FINAL_10;
        
        if (quizState.completed) {
          const percentage = Math.round((quizState.score / quizData.length) * 100);
          return (
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ color: slide.color, marginBottom: '1rem' }}>Quiz terminé !</h2>
              <div style={{
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: percentage >= 70 ? '#10B98120' : '#EF444420',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <span style={{ fontSize: '2.5rem', fontWeight: 'bold', color: percentage >= 70 ? '#10B981' : '#EF4444' }}>{percentage}%</span>
                <span style={{ color: '#666' }}>{quizState.score}/{quizData.length}</span>
              </div>
              <p style={{ color: percentage >= 70 ? '#10B981' : '#EF4444', fontWeight: 600 }}>
                {percentage >= 70 ? 'Félicitations ! Vous avez réussi !' : 'Continuez à réviser pour atteindre 70%'}
              </p>
            </div>
          );
        }

        const currentQ = quizData[quizState.currentQuestion];
        return (
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: slide.color }}>{slide.title}</h3>
              <span style={{ background: `${slide.color}20`, padding: '0.5rem 1rem', borderRadius: '1rem', color: slide.color }}>
                {quizState.currentQuestion + 1}/{quizData.length}
              </span>
            </div>
            
            <div style={{ background: `${slide.color}08`, padding: '1.5rem', borderRadius: '1rem', marginBottom: '1rem' }}>
              <p style={{ fontWeight: 600, color: '#333', marginBottom: '1rem' }}>{currentQ.question}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {currentQ.options.map((option, index) => {
                  let bgColor = '#fff';
                  let borderColor = '#ddd';
                  if (quizState.showResult) {
                    if (index === currentQ.correct) {
                      bgColor = '#10B98120';
                      borderColor = '#10B981';
                    } else if (index === quizState.selectedAnswer && index !== currentQ.correct) {
                      bgColor = '#EF444420';
                      borderColor = '#EF4444';
                    }
                  } else if (index === quizState.selectedAnswer) {
                    bgColor = `${slide.color}20`;
                    borderColor = slide.color;
                  }
                  
                  return (
                    <div
                      key={index}
                      onClick={() => handleQuizAnswer(index)}
                      style={{
                        padding: '1rem',
                        background: bgColor,
                        border: `2px solid ${borderColor}`,
                        borderRadius: '0.75rem',
                        cursor: quizState.showResult ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}
                    >
                      <span style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        background: borderColor === '#ddd' ? '#f0f0f0' : borderColor,
                        color: borderColor === '#ddd' ? '#666' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '0.85rem'
                      }}>{String.fromCharCode(65 + index)}</span>
                      <span>{option}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {quizState.showResult && (
              <div style={{
                padding: '1rem',
                background: quizState.selectedAnswer === currentQ.correct ? '#10B98110' : '#F59E0B10',
                borderRadius: '0.75rem',
                marginBottom: '1rem'
              }}>
                <p style={{ color: '#666', margin: 0 }}>💡 {currentQ.explanation}</p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {!quizState.showResult ? (
                <button
                  onClick={handleValidateAnswer}
                  disabled={quizState.selectedAnswer === null}
                  style={{
                    padding: '0.75rem 2rem',
                    background: quizState.selectedAnswer === null ? '#ccc' : slide.color,
                    color: 'white',
                    border: 'none',
                    borderRadius: '2rem',
                    cursor: quizState.selectedAnswer === null ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  Valider ma réponse
                </button>
              ) : (
                <button
                  onClick={handleNextQuestion}
                  style={{
                    padding: '0.75rem 2rem',
                    background: slide.color,
                    color: 'white',
                    border: 'none',
                    borderRadius: '2rem',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  {quizState.currentQuestion < quizData.length - 1 ? 'Question suivante' : 'Voir le résultat'}
                </button>
              )}
            </div>
          </div>
        );

      case 'development':
        return (
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <div style={{ 
              display: 'inline-block',
              background: `${slide.color}15`,
              padding: '0.5rem 1.5rem',
              borderRadius: '2rem',
              color: slide.color,
              fontWeight: 600,
              marginBottom: '1.5rem'
            }}>
              {slide.ageRange}
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              {slide.developments.map((dev, index) => (
                <div key={index} style={{
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '1rem',
                  borderLeft: `4px solid ${slide.color}`,
                  textAlign: 'left'
                }}>
                  <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.5rem' }}>{dev.icon}</span>
                  <span style={{ color: '#333', fontWeight: 500 }}>{dev.text}</span>
                </div>
              ))}
            </div>
            
            {slide.explanation && (
              <div style={{
                padding: '1.25rem',
                background: `${slide.color}10`,
                borderRadius: '1rem',
                marginBottom: '1rem',
                borderLeft: `4px solid ${slide.color}`
              }}>
                <p style={{ color: '#333', margin: 0, textAlign: 'left', lineHeight: 1.6 }}>{slide.explanation}</p>
              </div>
            )}
            
            {slide.keyPoints && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                {slide.keyPoints.map((point, index) => (
                  <div key={index} style={{
                    padding: '0.75rem 1.25rem',
                    background: slide.color,
                    color: 'white',
                    borderRadius: '2rem',
                    fontWeight: 500
                  }}>
                    ✓ {point}
                  </div>
                ))}
              </div>
            )}
            
            {slide.conclusion && (
              <p style={{ color: slide.color, fontWeight: 600, fontSize: '1.1rem', marginTop: '1rem' }}>{slide.conclusion}</p>
            )}
          </div>
        );

      case 'freeplay':
        return (
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '1rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '1.1rem' }}>{slide.intro}</p>
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              {slide.actions.map((action, index) => (
                <div key={index} style={{
                  padding: '0.75rem 1.5rem',
                  background: `${slide.color}15`,
                  border: `2px solid ${slide.color}40`,
                  borderRadius: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ color: slide.color, fontWeight: 'bold' }}>{action.icon}</span>
                  <span style={{ color: '#333', fontWeight: 500 }}>{action.text}</span>
                </div>
              ))}
            </div>
            
            <div style={{
              padding: '1.5rem',
              background: `linear-gradient(135deg, ${slide.color}15 0%, ${slide.color}25 100%)`,
              borderRadius: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ color: slide.color, fontWeight: 600, fontSize: '1.15rem', margin: 0 }}>
                {slide.highlight}
              </p>
            </div>
            
            <div style={{
              padding: '1rem 1.5rem',
              background: '#f8f9fa',
              borderRadius: '0.75rem',
              borderLeft: `4px solid ${slide.color}`,
              marginBottom: '1rem'
            }}>
              <p style={{ color: '#666', margin: 0, fontStyle: 'italic' }}>👩‍⚕️ {slide.adultRole}</p>
            </div>
            
            <p style={{ color: '#333', fontWeight: 500 }}>{slide.conclusion}</p>
          </div>
        );

      case 'attachment':
        return (
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `${slide.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '2.5rem'
            }}>
              ❤️
            </div>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            
            <div style={{
              padding: '1.5rem',
              background: `${slide.color}10`,
              borderRadius: '1rem',
              marginBottom: '1.5rem',
              borderLeft: `4px solid ${slide.color}`
            }}>
              <p style={{ color: '#333', fontWeight: 600, fontSize: '1.15rem', margin: 0 }}>{slide.mainConcept}</p>
            </div>
            
            <div style={{
              padding: '1.25rem',
              background: '#f8f9fa',
              borderRadius: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ color: '#666', margin: '0 0 0.75rem', lineHeight: 1.6 }}>📚 {slide.theory}</p>
              <p style={{ color: slide.color, fontWeight: 600, margin: 0 }}>→ {slide.keyPoint}</p>
            </div>
            
            <div style={{
              padding: '1rem',
              background: `linear-gradient(135deg, ${slide.color}10 0%, ${slide.color}20 100%)`,
              borderRadius: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ color: '#333', margin: 0 }}>{slide.explanation}</p>
            </div>
            
            <div style={{
              padding: '1rem 1.5rem',
              background: slide.color,
              color: 'white',
              borderRadius: '2rem',
              display: 'inline-block',
              fontWeight: 600
            }}>
              {slide.conclusion}
            </div>
          </div>
        );

      case 'selfesteem':
        return (
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `${slide.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '2.5rem'
            }}>
              ⭐
            </div>
            <h2 style={{ color: slide.color, marginBottom: '1rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '1.1rem' }}>{slide.intro}</p>
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
              {slide.achievements.map((achievement, index) => (
                <div key={index} style={{
                  padding: '1rem 1.25rem',
                  background: '#f8f9fa',
                  borderRadius: '1rem',
                  border: `2px solid ${slide.color}30`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  transition: 'all 0.2s ease'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>{achievement.icon}</span>
                  <span style={{ color: '#333', fontWeight: 500 }}>{achievement.text}</span>
                </div>
              ))}
            </div>
            
            <div style={{
              padding: '1.5rem',
              background: `linear-gradient(135deg, ${slide.color}15 0%, ${slide.color}25 100%)`,
              borderRadius: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ color: slide.color, fontWeight: 700, fontSize: '1.25rem', margin: 0 }}>
                💪 {slide.result}
              </p>
            </div>
            
            <p style={{ color: '#333', fontSize: '1.05rem', lineHeight: 1.6 }}>{slide.conclusion}</p>
          </div>
        );

      case 'aprole':
        return (
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `${slide.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '2.5rem'
            }}>
              👩‍⚕️
            </div>
            <h2 style={{ color: slide.color, marginBottom: '1rem' }}>{slide.title}</h2>
            
            <div style={{
              padding: '1rem 1.5rem',
              background: '#FEF3C7',
              borderRadius: '0.75rem',
              border: '2px solid #F59E0B',
              marginBottom: '1.5rem',
              display: 'inline-block'
            }}>
              <p style={{ color: '#92400E', fontWeight: 600, margin: 0 }}>⚠️ {slide.warning}</p>
            </div>
            
            <p style={{ color: '#666', marginBottom: '1rem', fontSize: '1.1rem' }}>{slide.intro}</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {slide.actions.map((action, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem 1.25rem',
                  background: `${slide.color}08`,
                  borderRadius: '0.75rem',
                  borderLeft: `4px solid ${slide.color}`
                }}>
                  <span style={{ fontSize: '1.5rem' }}>{action.icon}</span>
                  <span style={{ color: '#333', fontWeight: 500 }}>{action.text}</span>
                </div>
              ))}
            </div>
            
            <div style={{
              padding: '1rem 1.5rem',
              background: slide.color,
              color: 'white',
              borderRadius: '2rem',
              fontWeight: 600,
              display: 'inline-block'
            }}>
              ✨ {slide.conclusion}
            </div>
          </div>
        );

      case 'conclusion':
        return (
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <div style={{
              width: '90px',
              height: '90px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${slide.color}30 0%, ${slide.color}50 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '2.5rem'
            }}>
              🧪
            </div>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '1.05rem' }}>{slide.intro}</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {slide.points.map((point, index) => (
                <div key={index} style={{
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '1rem',
                  borderBottom: `3px solid ${slide.color}`
                }}>
                  <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.5rem' }}>{point.icon}</span>
                  <span style={{ color: '#333', fontWeight: 500, fontSize: '0.9rem' }}>{point.text}</span>
                </div>
              ))}
            </div>
            
            <div style={{
              padding: '1.5rem 2rem',
              background: `linear-gradient(135deg, ${slide.color}15 0%, ${slide.color}30 100%)`,
              borderRadius: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ color: slide.color, fontWeight: 700, fontSize: '1.3rem', margin: 0 }}>
                🔬 {slide.highlight}
              </p>
            </div>
            
            <div style={{
              padding: '1rem 1.5rem',
              background: '#f8f9fa',
              borderRadius: '0.75rem',
              borderLeft: `4px solid ${slide.color}`
            }}>
              <p style={{ color: '#333', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>{slide.finalNote}</p>
            </div>
          </div>
        );

      case 'activity':
        return (
          <div style={{ textAlign: 'center' }}>
            {slide.image ? (
              <div style={{ 
                display: 'flex', 
                gap: '2rem', 
                maxWidth: '950px', 
                margin: '0 auto',
                alignItems: 'flex-start'
              }}>
                {/* Image à gauche */}
                <div style={{ 
                  flex: '0 0 280px',
                  borderRadius: '1rem',
                  overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                }}>
                  <img 
                    src={slide.image} 
                    alt={slide.title}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </div>
                {/* Contenu à droite */}
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '2rem' }}>{slide.icon}</span>
                    <h2 style={{ color: slide.color, margin: 0 }}>{slide.title}</h2>
                  </div>
                  <p style={{ color: '#888', marginBottom: '1rem' }}>{slide.subtitle}</p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    {slide.proposals && (
                      <div>
                        <h4 style={{ color: slide.color, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Ce que je propose</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {slide.proposals.map((p, i) => (
                            <li key={i} style={{ padding: '0.35rem 0', borderBottom: '1px solid #eee', fontSize: '0.9rem' }}>• {p}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div>
                      <h4 style={{ color: slide.color, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Ce que cela développe</h4>
                      {slide.developments.map((d, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', fontSize: '0.9rem' }}>
                          <span>{d.icon}</span>
                          <span>{d.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {slide.role && (
                    <div style={{ marginTop: '1rem', background: `${slide.color}10`, padding: '0.75rem', borderRadius: '0.5rem' }}>
                      <h4 style={{ color: slide.color, marginBottom: '0.5rem', fontSize: '0.85rem' }}>Rôle professionnel</h4>
                      {slide.role.map((r, i) => (
                        <p key={i} style={{ margin: '0.2rem 0', color: '#666', fontSize: '0.85rem' }}>✓ {r}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>{slide.icon}</div>
                <h2 style={{ color: slide.color, marginBottom: '0.25rem' }}>{slide.title}</h2>
                <p style={{ color: '#888', marginBottom: '1.5rem' }}>{slide.subtitle}</p>
                
                <div style={{ display: 'grid', gridTemplateColumns: slide.proposals ? '1fr 1fr' : '1fr', gap: '1.5rem', maxWidth: '800px', margin: '0 auto', textAlign: 'left' }}>
                  {slide.proposals && (
                    <div>
                      <h4 style={{ color: slide.color, marginBottom: '0.75rem' }}>Ce que je propose</h4>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {slide.proposals.map((p, i) => (
                          <li key={i} style={{ padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>• {p}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <h4 style={{ color: slide.color, marginBottom: '0.75rem' }}>Ce que cela développe</h4>
                    {slide.developments.map((d, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0' }}>
                        <span>{d.icon}</span>
                        <span>{d.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {slide.role && (
                  <div style={{ marginTop: '1.5rem', background: `${slide.color}10`, padding: '1rem', borderRadius: '0.75rem', maxWidth: '500px', margin: '1.5rem auto 0' }}>
                    <h4 style={{ color: slide.color, marginBottom: '0.5rem' }}>Rôle professionnel</h4>
                    {slide.role.map((r, i) => (
                      <p key={i} style={{ margin: '0.25rem 0', color: '#666' }}>✓ {r}</p>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 'rituals':
        return (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', maxWidth: '800px', margin: '0 auto' }}>
              <div>
                <h4 style={{ color: slide.color, marginBottom: '1rem' }}>Exemples de rituels</h4>
                {slide.rituals.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: `${slide.color}08`, borderRadius: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>{r.icon}</span>
                    <span>{r.text}</span>
                  </div>
                ))}
              </div>
              <div>
                <h4 style={{ color: slide.color, marginBottom: '1rem' }}>Pourquoi les rituels ?</h4>
                {slide.whyImportant.map((w, i) => (
                  <div key={i} style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', marginBottom: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
                    ✓ {w}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: '1.5rem', padding: '1rem', background: `${slide.color}10`, borderRadius: '0.75rem', maxWidth: '600px', margin: '1.5rem auto 0' }}>
              <p style={{ color: '#666', fontStyle: 'italic', margin: 0 }}>📚 {slide.theory}</p>
            </div>
          </div>
        );

      case 'jury':
        return (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: slide.color, marginBottom: '1rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>{slide.intro}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '600px', margin: '0 auto' }}>
              {slide.criteria.map((c, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  background: '#10B98110',
                  borderRadius: '0.75rem',
                  border: '2px solid #10B98140'
                }}>
                  <span style={{ color: '#10B981', fontSize: '1.5rem' }}>{c.icon}</span>
                  <span style={{ color: '#333', textAlign: 'left' }}>{c.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'spaces':
        return (
          <div style={{ textAlign: 'center', maxWidth: '950px', margin: '0 auto' }}>
            {slide.image ? (
              <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
                <div style={{ 
                  flex: '0 0 320px',
                  borderRadius: '1rem',
                  overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                }}>
                  <img 
                    src={slide.image} 
                    alt={slide.title}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <h2 style={{ color: slide.color, marginBottom: '1rem' }}>{slide.title}</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
                    {slide.spaces.map((space, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        background: `${slide.color}10`,
                        borderRadius: '0.5rem'
                      }}>
                        <span style={{ fontSize: '1.25rem' }}>{space.icon}</span>
                        <span style={{ color: '#333', fontSize: '0.9rem' }}>{space.text}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '0.75rem' }}>
                    <h4 style={{ color: slide.color, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Principes clés</h4>
                    {slide.principles.map((p, i) => (
                      <p key={i} style={{ margin: '0.25rem 0', color: '#666', fontSize: '0.85rem' }}>✓ {p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                  {slide.spaces.map((space, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '1rem',
                      background: `${slide.color}10`,
                      borderRadius: '0.75rem'
                    }}>
                      <span style={{ fontSize: '1.5rem' }}>{space.icon}</span>
                      <span style={{ color: '#333' }}>{space.text}</span>
                    </div>
                  ))}
                </div>
                <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '1rem', maxWidth: '500px', margin: '0 auto' }}>
                  <h4 style={{ color: slide.color, marginBottom: '0.75rem' }}>Principes clés</h4>
                  {slide.principles.map((p, i) => (
                    <p key={i} style={{ margin: '0.5rem 0', color: '#666' }}>✓ {p}</p>
                  ))}
                </div>
              </>
            )}
          </div>
        );

      case 'play_synthesis':
        return (
          <div style={{ 
            maxWidth: '900px', 
            margin: '0 auto',
            padding: '1.5rem',
            background: `linear-gradient(135deg, ${slide.color}10 0%, ${slide.color}05 100%)`,
            borderRadius: '1.5rem',
            border: `2px solid ${slide.color}30`
          }}>
            {/* Message principal */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ 
                color: slide.color, 
                fontSize: '1.8rem', 
                marginBottom: '0.5rem',
                textShadow: `0 2px 4px ${slide.color}20`
              }}>{slide.title}</h2>
              <div style={{
                display: 'inline-block',
                padding: '0.75rem 2rem',
                background: `linear-gradient(135deg, ${slide.color}, ${slide.color}CC)`,
                borderRadius: '2rem',
                color: 'white',
                fontSize: '1.3rem',
                fontWeight: '700',
                boxShadow: `0 4px 15px ${slide.color}40`
              }}>
                {slide.mainMessage}
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '1.5rem' }}>
              {/* Pour l'enfant */}
              <div style={{
                padding: '1.25rem',
                background: 'white',
                borderRadius: '1rem',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '2rem' }}>{slide.forChild.icon}</span>
                  <h3 style={{ color: slide.color, margin: 0, fontSize: '1.1rem' }}>{slide.forChild.title}</h3>
                </div>
                {slide.forChild.points.map((point, i) => (
                  <div key={i} style={{
                    padding: '0.5rem 0.75rem',
                    marginBottom: '0.5rem',
                    background: `${slide.color}08`,
                    borderRadius: '0.5rem',
                    borderLeft: `3px solid ${slide.color}`
                  }}>
                    <span style={{ color: '#444', fontSize: '0.9rem' }}>✨ {point}</span>
                  </div>
                ))}
              </div>
              
              {/* Apprentissages */}
              <div style={{
                padding: '1.25rem',
                background: 'white',
                borderRadius: '1rem',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
              }}>
                <h3 style={{ color: slide.color, marginBottom: '1rem', fontSize: '1.1rem' }}>{slide.learnings.title}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                  {slide.learnings.items.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      background: `${slide.color}08`,
                      borderRadius: '0.75rem',
                      border: `1px solid ${slide.color}20`
                    }}>
                      <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                      <span style={{ color: '#333', fontSize: '0.85rem', fontWeight: '500' }}>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Citation */}
            <div style={{
              marginTop: '1.5rem',
              padding: '1rem 1.5rem',
              background: `linear-gradient(135deg, ${slide.color}15, ${slide.color}05)`,
              borderRadius: '1rem',
              textAlign: 'center',
              borderLeft: `4px solid ${slide.color}`
            }}>
              <p style={{ 
                color: '#555', 
                fontSize: '1.1rem', 
                fontStyle: 'italic', 
                margin: 0 
              }}>{slide.quote}</p>
            </div>
          </div>
        );

      case 'final_conclusion':
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '2rem' }}>{slide.message}</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', maxWidth: '700px', margin: '0 auto' }}>
              <div style={{ background: `${slide.color}10`, padding: '1.5rem', borderRadius: '1rem' }}>
                <h4 style={{ color: slide.color, marginBottom: '1rem' }}>Points clés à retenir</h4>
                {slide.summary.map((s, i) => (
                  <p key={i} style={{ margin: '0.5rem 0', color: '#333' }}>✓ {s}</p>
                ))}
              </div>
              <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '1rem' }}>
                <h4 style={{ color: '#666', marginBottom: '1rem' }}>Prochaines étapes</h4>
                {slide.nextSteps.map((s, i) => (
                  <p key={i} style={{ margin: '0.5rem 0', color: '#666' }}>→ {s}</p>
                ))}
              </div>
            </div>
          </div>
        );

      case 'fundamentals':
        return (
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>{slide.intro}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              {slide.influences.map((item, i) => (
                <div key={i} style={{
                  padding: '1rem',
                  background: `${slide.color}10`,
                  borderRadius: '1rem',
                  border: `2px solid ${slide.color}30`
                }}>
                  <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.5rem' }}>{item.icon}</span>
                  <span style={{ color: '#333', fontWeight: 500 }}>{item.text}</span>
                </div>
              ))}
            </div>
            <div style={{
              padding: '1.25rem 2rem',
              background: `linear-gradient(135deg, ${slide.color}20 0%, ${slide.color}40 100%)`,
              borderRadius: '1rem'
            }}>
              <p style={{ color: slide.color, fontWeight: 700, fontSize: '1.2rem', margin: 0 }}>{slide.conclusion}</p>
            </div>
          </div>
        );

      case 'dev_support':
        return (
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: `${slide.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '2rem'
            }}>
              {slide.icon}
            </div>
            <h2 style={{ color: slide.color, marginBottom: '0.25rem' }}>{slide.title}</h2>
            <p style={{ color: '#888', marginBottom: '1rem' }}>{slide.subtitle}</p>
            <p style={{ color: '#666', marginBottom: '1rem' }}>{slide.intro}</p>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {slide.points.map((point, i) => (
                <div key={i} style={{
                  padding: '0.75rem 1.25rem',
                  background: `${slide.color}15`,
                  borderRadius: '2rem',
                  color: '#333',
                  fontWeight: 500
                }}>
                  {point}
                </div>
              ))}
            </div>
            
            {slide.examples && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                {slide.examples.map((ex, i) => (
                  <div key={i} style={{
                    padding: '1rem 1.5rem',
                    background: '#f8f9fa',
                    borderRadius: '0.75rem',
                    borderLeft: `4px solid ${slide.color}`
                  }}>
                    <span style={{ color: slide.color, fontWeight: 600, display: 'block' }}>{ex.age}</span>
                    <span style={{ color: '#666' }}>{ex.item}</span>
                  </div>
                ))}
              </div>
            )}
            
            {slide.symbolics && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '1rem' }}>
                {slide.symbolics.map((s, i) => (
                  <div key={i} style={{
                    padding: '0.75rem 1.25rem',
                    background: '#f8f9fa',
                    borderRadius: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.25rem' }}>{s.icon}</span>
                    <span style={{ color: '#666' }}>{s.item}</span>
                  </div>
                ))}
              </div>
            )}
            
            {slide.theory && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem 1.5rem',
                background: `${slide.color}10`,
                borderRadius: '1rem',
                display: 'inline-block'
              }}>
                <p style={{ color: slide.color, fontWeight: 600, margin: 0 }}>👉 {slide.theory}</p>
              </div>
            )}
          </div>
        );

      case 'age_adaptation':
        return (
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem 1.5rem',
              background: `${slide.color}15`,
              borderRadius: '2rem',
              marginBottom: '1rem'
            }}>
              <span style={{ fontSize: '1.5rem' }}>{slide.icon}</span>
              <h2 style={{ color: slide.color, margin: 0 }}>{slide.title}</h2>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
              <div style={{ textAlign: 'left', padding: '1.25rem', background: '#f8f9fa', borderRadius: '1rem' }}>
                <h4 style={{ color: slide.color, marginBottom: '0.75rem' }}>Besoins</h4>
                {slide.needs.map((need, i) => (
                  <p key={i} style={{ margin: '0.5rem 0', color: '#333' }}>• {need}</p>
                ))}
              </div>
              <div style={{ textAlign: 'left', padding: '1.25rem', background: `${slide.color}10`, borderRadius: '1rem' }}>
                <h4 style={{ color: slide.color, marginBottom: '0.75rem' }}>Aménagement</h4>
                {slide.setup.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0' }}>
                    <span>{item.icon}</span>
                    <span style={{ color: '#333' }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {slide.warning && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem 1.5rem',
                background: '#FEF3C7',
                borderRadius: '0.75rem',
                border: '2px solid #F59E0B'
              }}>
                <p style={{ color: '#92400E', fontWeight: 600, margin: 0 }}>⚠️ {slide.warning}</p>
              </div>
            )}
          </div>
        );

      case 'norms':
        return (
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: `${slide.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '2rem'
            }}>
              {slide.icon}
            </div>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {slide.norms.map((norm, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem 1.25rem',
                  background: `${slide.color}08`,
                  borderRadius: '0.75rem',
                  borderLeft: `4px solid ${slide.color}`
                }}>
                  <span style={{ fontSize: '1.25rem' }}>{norm.icon}</span>
                  <span style={{ color: '#333', textAlign: 'left' }}>{norm.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'quality_games':
        return (
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>{slide.intro}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {slide.criteria.map((c, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem',
                  background: '#10B98110',
                  borderRadius: '0.75rem',
                  border: '2px solid #10B98140'
                }}>
                  <span style={{ color: '#10B981', fontSize: '1.25rem' }}>{c.icon}</span>
                  <span style={{ color: '#333', textAlign: 'left' }}>{c.text}</span>
                </div>
              ))}
            </div>
            <div style={{
              padding: '1rem 1.5rem',
              background: '#FEF3C7',
              borderRadius: '0.75rem',
              border: '2px solid #F59E0B'
            }}>
              <p style={{ color: '#92400E', fontWeight: 600, margin: 0 }}>⚠️ {slide.warning}</p>
            </div>
          </div>
        );

      case 'prevention_tool':
        return (
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              <div style={{ padding: '1.5rem', background: '#FEE2E2', borderRadius: '1rem' }}>
                <h4 style={{ color: '#DC2626', marginBottom: '1rem' }}>Réduit</h4>
                {slide.reduces.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0' }}>
                    <span style={{ color: '#DC2626' }}>{item.icon}</span>
                    <span style={{ color: '#7F1D1D' }}>{item.text}</span>
                  </div>
                ))}
              </div>
              <div style={{ padding: '1.5rem', background: '#D1FAE5', borderRadius: '1rem' }}>
                <h4 style={{ color: '#059669', marginBottom: '1rem' }}>Favorise</h4>
                {slide.promotes.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0' }}>
                    <span style={{ color: '#059669' }}>{item.icon}</span>
                    <span style={{ color: '#064E3B' }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'ap_amenagement':
        return (
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `${slide.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '2.5rem'
            }}>
              👩‍⚕️
            </div>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>{slide.intro}</p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {slide.actions.map((action, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  background: `${slide.color}10`,
                  borderRadius: '0.75rem',
                  borderLeft: `4px solid ${slide.color}`
                }}>
                  <span style={{ fontSize: '1.25rem' }}>{action.icon}</span>
                  <span style={{ color: '#333', textAlign: 'left' }}>{action.text}</span>
                </div>
              ))}
            </div>
            
            <div style={{
              padding: '1rem 2rem',
              background: slide.color,
              color: 'white',
              borderRadius: '2rem',
              display: 'inline-block',
              fontWeight: 600
            }}>
              ✨ {slide.conclusion}
            </div>
          </div>
        );

      case 'identity_link':
        return (
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>{slide.intro}</p>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              {slide.points.map((point, i) => (
                <div key={i} style={{
                  padding: '1rem 1.5rem',
                  background: `${slide.color}10`,
                  borderRadius: '1rem',
                  border: `2px solid ${slide.color}30`
                }}>
                  <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.25rem' }}>{point.icon}</span>
                  <span style={{ color: '#333', fontWeight: 500 }}>{point.text}</span>
                </div>
              ))}
            </div>
            
            <div style={{
              padding: '1.25rem 2rem',
              background: `linear-gradient(135deg, ${slide.color}15 0%, ${slide.color}30 100%)`,
              borderRadius: '1rem'
            }}>
              <p style={{ color: slide.color, fontWeight: 700, fontSize: '1.15rem', margin: 0 }}>👉 {slide.conclusion}</p>
            </div>
          </div>
        );

      case 'jury_amenagement':
        return (
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: `${slide.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '2rem'
            }}>
              🎓
            </div>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>{slide.intro}</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {slide.expectations.map((exp, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem 1.25rem',
                  background: `${slide.color}08`,
                  borderRadius: '0.75rem',
                  borderLeft: `4px solid ${slide.color}`
                }}>
                  <span style={{ color: '#10B981', fontSize: '1.25rem' }}>{exp.icon}</span>
                  <span style={{ color: '#333', textAlign: 'left' }}>{exp.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'vae_example':
        return (
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '2rem' }}>{slide.title}</h2>
            
            <div style={{ marginBottom: '1.5rem', padding: '1.5rem', background: '#FEE2E2', borderRadius: '1rem', border: '2px solid #EF4444' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>❌</span>
                <span style={{ color: '#DC2626', fontWeight: 600 }}>À éviter</span>
              </div>
              <p style={{ color: '#7F1D1D', margin: 0, fontStyle: 'italic', fontSize: '1.1rem' }}>"{slide.wrong}"</p>
            </div>
            
            <div style={{ padding: '1.5rem', background: '#D1FAE5', borderRadius: '1rem', border: '2px solid #10B981' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.5rem' }}>✔</span>
                <span style={{ color: '#059669', fontWeight: 600 }}>Formulation professionnelle</span>
              </div>
              <p style={{ color: '#064E3B', margin: 0, fontSize: '1.05rem', lineHeight: 1.6, textAlign: 'left' }}>"{slide.correct}"</p>
            </div>
          </div>
        );

      case 'quiz_amenagement':
        const quizAmenAnswer = quizState.selectedAnswer;
        const showAmenExp = quizState.showResult;
        return (
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: `${slide.color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
              fontSize: '1.5rem'
            }}>
              ❓
            </div>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#333', marginBottom: '1.5rem', fontSize: '1.1rem', fontWeight: 500 }}>{slide.question}</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {slide.options.map((option) => {
                const isSelected = quizAmenAnswer === option.id;
                const isCorrectOption = option.correct;
                let bgColor = '#f8f9fa';
                let borderColor = '#ddd';
                
                if (showAmenExp) {
                  if (isCorrectOption) {
                    bgColor = '#D1FAE5';
                    borderColor = '#10B981';
                  } else if (isSelected && !isCorrectOption) {
                    bgColor = '#FEE2E2';
                    borderColor = '#EF4444';
                  }
                } else if (isSelected) {
                  bgColor = `${slide.color}15`;
                  borderColor = slide.color;
                }
                
                return (
                  <button
                    key={option.id}
                    onClick={() => {
                      if (!showAmenExp) {
                        setQuizState(prev => ({
                          ...prev,
                          selectedAnswer: option.id,
                          showResult: true,
                          score: option.correct ? prev.score + 1 : prev.score
                        }));
                      }
                    }}
                    disabled={showAmenExp}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '1rem 1.25rem',
                      background: bgColor,
                      border: `2px solid ${borderColor}`,
                      borderRadius: '0.75rem',
                      cursor: showAmenExp ? 'default' : 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: isSelected ? slide.color : '#e5e7eb',
                      color: isSelected ? 'white' : '#666',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600
                    }}>
                      {option.id.toUpperCase()}
                    </span>
                    <span style={{ color: '#333' }}>{option.text}</span>
                  </button>
                );
              })}
            </div>
            
            {showAmenExp && (
              <div style={{
                marginTop: '1.5rem',
                padding: '1.25rem',
                background: slide.options.find(o => o.id === quizAmenAnswer)?.correct ? '#D1FAE5' : '#FEF3C7',
                borderRadius: '1rem',
                border: `2px solid ${slide.options.find(o => o.id === quizAmenAnswer)?.correct ? '#10B981' : '#F59E0B'}`
              }}>
                <p style={{ color: slide.options.find(o => o.id === quizAmenAnswer)?.correct ? '#059669' : '#92400E', fontWeight: 600, margin: '0 0 0.5rem' }}>
                  {slide.options.find(o => o.id === quizAmenAnswer)?.correct ? '✓ Bonne réponse !' : '✗ Pas tout à fait...'}
                </p>
                <p style={{ color: '#333', margin: 0 }}>{slide.explanation}</p>
              </div>
            )}
          </div>
        );

      case 'jeu_libre_definition':
        return (
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>{slide.intro}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              {slide.points.map((point, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '1rem', background: `${slide.color}10`, borderRadius: '0.75rem',
                  borderLeft: `4px solid ${slide.color}`
                }}>
                  <span style={{ fontSize: '1.5rem' }}>{point.icon}</span>
                  <span style={{ color: '#333' }}>{point.text}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: '1rem 1.5rem', background: '#FEF3C7', borderRadius: '0.75rem', marginBottom: '1rem' }}>
              <p style={{ color: '#92400E', fontWeight: 600, margin: 0 }}>{slide.emphasis}</p>
            </div>
            <p style={{ color: slide.color, fontWeight: 600 }}>{slide.conclusion}</p>
          </div>
        );

      case 'jeu_libre_dev':
        return (
          <div style={{ textAlign: 'center', maxWidth: '750px', margin: '0 auto' }}>
            <div style={{
              width: '70px', height: '70px', borderRadius: '50%',
              background: `${slide.color}20`, display: 'flex', alignItems: 'center',
              justifyContent: 'center', margin: '0 auto 1rem', fontSize: '2rem'
            }}>{slide.icon}</div>
            <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>{slide.intro}</p>
            
            {slide.points && (
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {slide.points.map((point, i) => (
                  <div key={i} style={{
                    padding: '0.75rem 1.25rem', background: `${slide.color}15`,
                    borderRadius: '2rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
                  }}>
                    <span>{point.icon}</span>
                    <span style={{ color: '#333', fontWeight: 500 }}>{point.text}</span>
                  </div>
                ))}
              </div>
            )}
            
            {slide.socialPoints && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1rem' }}>
                <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '1rem' }}>
                  <h4 style={{ color: slide.color, marginBottom: '0.75rem' }}>Social</h4>
                  {slide.socialPoints.map((p, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0' }}>
                      <span>{p.icon}</span><span style={{ color: '#666' }}>{p.text}</span>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '1rem', background: `${slide.color}10`, borderRadius: '1rem' }}>
                  <h4 style={{ color: slide.color, marginBottom: '0.75rem' }}>Autonomie</h4>
                  {slide.autonomyPoints.map((p, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0' }}>
                      <span>{p.icon}</span><span style={{ color: '#666' }}>{p.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {slide.theory && (
              <div style={{ padding: '1rem 1.5rem', background: `${slide.color}10`, borderRadius: '1rem', marginBottom: '1rem' }}>
                <p style={{ color: slide.color, fontWeight: 600, margin: 0 }}>{slide.theory}</p>
              </div>
            )}
            
            {slide.highlight && (
              <div style={{ padding: '1rem 1.5rem', background: `linear-gradient(135deg, ${slide.color}20 0%, ${slide.color}40 100%)`, borderRadius: '1rem', marginBottom: '1rem' }}>
                <p style={{ color: slide.color, fontWeight: 700, fontSize: '1.1rem', margin: 0 }}>{slide.highlight}</p>
              </div>
            )}
            
            {slide.conclusion && (
              <p style={{ color: slide.color, fontWeight: 600 }}>{slide.conclusion}</p>
            )}
          </div>
        );

      case 'ap_jeu_libre':
        return (
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              {slide.missions.map((mission, i) => (
                <div key={i} style={{
                  padding: '1.25rem', background: `${slide.color}08`, borderRadius: '1rem',
                  borderTop: `4px solid ${slide.color}`, textAlign: 'left'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>{mission.icon}</span>
                    <h4 style={{ color: slide.color, margin: 0 }}>{mission.title}</h4>
                  </div>
                  {mission.points.map((p, j) => (
                    <p key={j} style={{ margin: '0.35rem 0', color: '#666', fontSize: '0.9rem' }}>• {p}</p>
                  ))}
                </div>
              ))}
            </div>
            {slide.note && (
              <p style={{ color: '#666', fontStyle: 'italic', marginTop: '1rem' }}>{slide.note}</p>
            )}
          </div>
        );

      case 'ap_jeu_libre_suite':
        return (
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              {slide.missions.map((mission, i) => (
                <div key={i} style={{
                  padding: '1.25rem', background: `${slide.color}08`, borderRadius: '1rem',
                  borderTop: `4px solid ${slide.color}`, textAlign: 'left'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>{mission.icon}</span>
                    <h4 style={{ color: slide.color, margin: 0 }}>{mission.title}</h4>
                  </div>
                  {mission.points && mission.points.map((p, j) => (
                    <p key={j} style={{ margin: '0.35rem 0', color: '#666', fontSize: '0.9rem' }}>• {p}</p>
                  ))}
                  {mission.avoid && (
                    <>
                      <p style={{ color: '#DC2626', fontWeight: 500, marginTop: '0.5rem', fontSize: '0.85rem' }}>Elle évite :</p>
                      {mission.avoid.map((a, j) => (
                        <p key={j} style={{ margin: '0.25rem 0', color: '#7F1D1D', fontSize: '0.85rem' }}>✗ {a}</p>
                      ))}
                    </>
                  )}
                  {mission.do && (
                    <p style={{ color: '#059669', marginTop: '0.5rem', fontSize: '0.9rem' }}>✓ {mission.do}</p>
                  )}
                  {mission.note && (
                    <p style={{ color: slide.color, fontStyle: 'italic', marginTop: '0.5rem', fontSize: '0.85rem' }}>{mission.note}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'equilibre_jeu_libre':
        return (
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '1.5rem' }}>
              <div style={{ padding: '1.5rem', background: '#FEE2E2', borderRadius: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{slide.tooMuch.icon}</span>
                  <h4 style={{ color: '#DC2626', margin: 0 }}>{slide.tooMuch.title}</h4>
                </div>
                {slide.tooMuch.effects.map((e, i) => (
                  <p key={i} style={{ margin: '0.5rem 0', color: '#7F1D1D' }}>• {e}</p>
                ))}
              </div>
              <div style={{ padding: '1.5rem', background: '#FEF3C7', borderRadius: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{slide.notEnough.icon}</span>
                  <h4 style={{ color: '#92400E', margin: 0 }}>{slide.notEnough.title}</h4>
                </div>
                {slide.notEnough.effects.map((e, i) => (
                  <p key={i} style={{ margin: '0.5rem 0', color: '#78350F' }}>• {e}</p>
                ))}
              </div>
            </div>
            <div style={{ padding: '1.25rem 2rem', background: `linear-gradient(135deg, ${slide.color}20 0%, ${slide.color}40 100%)`, borderRadius: '1rem' }}>
              <p style={{ color: slide.color, fontWeight: 700, fontSize: '1.15rem', margin: 0 }}>{slide.conclusion}</p>
            </div>
          </div>
        );

      case 'references_jeu_libre':
        return (
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
            <h2 style={{ color: slide.color, marginBottom: '1.5rem' }}>{slide.title}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {slide.references.map((ref, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  padding: '1rem 1.25rem', background: `${slide.color}08`, borderRadius: '0.75rem',
                  borderLeft: `4px solid ${slide.color}`
                }}>
                  <span style={{ color: slide.color, fontWeight: 700, minWidth: '100px' }}>{ref.author}</span>
                  <span style={{ color: '#666' }}>{ref.concept}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'baby_dev_complete':
        return (
          <div style={{ maxWidth: '950px', margin: '0 auto' }}>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
              {/* Image à gauche */}
              <div style={{ flex: '0 0 280px' }}>
                <div style={{ borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                  <img src={slide.image} alt={slide.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
                </div>
              </div>
              
              {/* Contenu à droite */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 1rem', background: `${slide.color}15`, borderRadius: '2rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>👶</span>
                  <h2 style={{ color: slide.color, margin: 0 }}>{slide.title}</h2>
                </div>
                <p style={{ color: '#666', marginBottom: '1rem', fontStyle: 'italic' }}>{slide.subtitle}</p>
                
                {/* Développement global */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '0.9rem' }}>🧠 Développement global</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
                      <span style={{ color: slide.color, fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>🔹 Moteur</span>
                      {slide.development.moteur.map((item, i) => (
                        <p key={i} style={{ margin: '0.2rem 0', color: '#666', fontSize: '0.75rem' }}>• {item}</p>
                      ))}
                    </div>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
                      <span style={{ color: slide.color, fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>🔹 Sensoriel</span>
                      {slide.development.sensoriel.map((item, i) => (
                        <p key={i} style={{ margin: '0.2rem 0', color: '#666', fontSize: '0.75rem' }}>• {item}</p>
                      ))}
                    </div>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
                      <span style={{ color: slide.color, fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>🔹 Relationnel</span>
                      {slide.development.relationnel.map((item, i) => (
                        <p key={i} style={{ margin: '0.2rem 0', color: '#666', fontSize: '0.75rem' }}>• {item}</p>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Actions d'éveil */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '0.9rem' }}>🎯 Actions d'éveil proposées</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {slide.activities.map((activity, i) => (
                      <span key={i} style={{ padding: '0.35rem 0.75rem', background: `${slide.color}10`, borderRadius: '1rem', color: '#333', fontSize: '0.8rem' }}>
                        ✔ {activity}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Posture professionnelle */}
                <div style={{ padding: '0.75rem', background: `${slide.color}10`, borderRadius: '0.75rem' }}>
                  <h4 style={{ color: slide.color, marginBottom: '0.5rem', fontSize: '0.85rem' }}>👩‍⚕️ Posture professionnelle</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {slide.posture.map((item, i) => (
                      <span key={i} style={{ color: '#666', fontSize: '0.8rem' }}>• {item}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'baby_dev_complete_v2':
        return (
          <div style={{ maxWidth: '950px', margin: '0 auto' }}>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
              {/* Image à gauche */}
              <div style={{ flex: '0 0 280px' }}>
                <div style={{ borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                  <img src={slide.image} alt={slide.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
                </div>
              </div>
              
              {/* Contenu à droite */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 1rem', background: `${slide.color}15`, borderRadius: '2rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>👶</span>
                  <h2 style={{ color: slide.color, margin: 0 }}>{slide.title}</h2>
                </div>
                <p style={{ color: '#666', marginBottom: '1rem', fontStyle: 'italic' }}>{slide.subtitle}</p>
                
                {/* Développement global */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '0.9rem' }}>🧠 Développement global</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
                      <span style={{ color: slide.color, fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>🔹 Moteur</span>
                      {slide.development.moteur.map((item, i) => (
                        <p key={i} style={{ margin: '0.2rem 0', color: '#666', fontSize: '0.75rem' }}>• {item}</p>
                      ))}
                    </div>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
                      <span style={{ color: slide.color, fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>🔹 Sensoriel</span>
                      {slide.development.sensoriel.map((item, i) => (
                        <p key={i} style={{ margin: '0.2rem 0', color: '#666', fontSize: '0.75rem' }}>• {item}</p>
                      ))}
                    </div>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
                      <span style={{ color: slide.color, fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>🔹 Cognitif</span>
                      {slide.development.cognitif.map((item, i) => (
                        <p key={i} style={{ margin: '0.2rem 0', color: '#666', fontSize: '0.75rem' }}>• {item}</p>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Actions d'éveil */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '0.9rem' }}>🎯 Actions d'éveil proposées</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {slide.activities.map((activity, i) => (
                      <span key={i} style={{ padding: '0.35rem 0.75rem', background: `${slide.color}10`, borderRadius: '1rem', color: '#333', fontSize: '0.8rem' }}>
                        ✔ {activity}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* À éviter */}
                <div style={{ padding: '0.75rem', background: '#FEE2E2', borderRadius: '0.75rem', border: '2px solid #EF4444' }}>
                  <h4 style={{ color: '#DC2626', marginBottom: '0.5rem', fontSize: '0.85rem' }}>🛑 À éviter</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                    {slide.toAvoid.map((item, i) => (
                      <span key={i} style={{ color: '#7F1D1D', fontSize: '0.8rem' }}>✗ {item}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'baby_dev_complete_v3':
        return (
          <div style={{ maxWidth: '950px', margin: '0 auto' }}>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
              {/* Image à gauche */}
              <div style={{ flex: '0 0 280px' }}>
                <div style={{ borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                  <img src={slide.image} alt={slide.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
                </div>
              </div>
              
              {/* Contenu à droite */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 1rem', background: `${slide.color}15`, borderRadius: '2rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>👶</span>
                  <h2 style={{ color: slide.color, margin: 0 }}>{slide.title}</h2>
                </div>
                <p style={{ color: '#666', marginBottom: '1rem', fontStyle: 'italic' }}>{slide.subtitle}</p>
                
                {/* Développement global */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '0.9rem' }}>🧠 Développement global</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
                      <span style={{ color: slide.color, fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>🔹 Moteur</span>
                      {slide.development.moteur.map((item, i) => (
                        <p key={i} style={{ margin: '0.2rem 0', color: '#666', fontSize: '0.75rem' }}>• {item}</p>
                      ))}
                    </div>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
                      <span style={{ color: slide.color, fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>🔹 Cognitif</span>
                      {slide.development.cognitif.map((item, i) => (
                        <p key={i} style={{ margin: '0.2rem 0', color: '#666', fontSize: '0.75rem' }}>• {item}</p>
                      ))}
                    </div>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '0.5rem', borderLeft: `3px solid ${slide.color}` }}>
                      <span style={{ color: slide.color, fontWeight: 600, fontSize: '0.8rem', display: 'block', marginBottom: '0.25rem' }}>🔹 Langage</span>
                      {slide.development.langage.map((item, i) => (
                        <p key={i} style={{ margin: '0.2rem 0', color: '#666', fontSize: '0.75rem' }}>• {item}</p>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Actions d'éveil */}
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ color: '#333', marginBottom: '0.5rem', fontSize: '0.9rem' }}>🎯 Actions d'éveil proposées</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {slide.activities.map((activity, i) => (
                      <span key={i} style={{ padding: '0.35rem 0.75rem', background: `${slide.color}10`, borderRadius: '1rem', color: '#333', fontSize: '0.8rem' }}>
                        ✔ {activity}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Domaines développés */}
                <div style={{ padding: '0.75rem', background: `${slide.color}10`, borderRadius: '0.75rem' }}>
                  <h4 style={{ color: slide.color, marginBottom: '0.5rem', fontSize: '0.85rem' }}>🧩 Domaines développés par l'éveil</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                    {slide.domains.map((domain, i) => (
                      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#666', fontSize: '0.8rem' }}>
                        <span>{domain.icon}</span> {domain.text}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'baby_security':
        return (
          <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
            {/* Titre principal */}
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.75rem', 
              padding: '0.75rem 1.5rem', 
              background: `${slide.color}15`, 
              borderRadius: '2rem', 
              marginBottom: '1.5rem' 
            }}>
              <span style={{ fontSize: '1.5rem' }}>🛡️</span>
              <h2 style={{ color: slide.color, margin: 0, fontSize: '1.5rem' }}>{slide.title}</h2>
            </div>
            
            {/* Message clé */}
            <div style={{ 
              padding: '1.5rem 2rem', 
              background: `linear-gradient(135deg, ${slide.color}10, ${slide.color}05)`, 
              borderRadius: '1rem', 
              marginBottom: '2rem',
              border: `2px solid ${slide.color}30`
            }}>
              <p style={{ 
                color: '#333', 
                fontSize: '1.25rem', 
                fontWeight: '600', 
                margin: 0,
                fontStyle: 'italic'
              }}>
                "{slide.subtitle}"
              </p>
            </div>
            
            {/* Rôle de l'adulte */}
            <h3 style={{ color: slide.color, marginBottom: '1.5rem', fontSize: '1.2rem' }}>{slide.adultRole}</h3>
            
            {/* Points */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
              {slide.points.map((point, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '1rem 2rem',
                  background: 'white',
                  borderRadius: '1rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  border: `1px solid ${slide.color}20`,
                  minWidth: '350px'
                }}>
                  <span style={{ 
                    fontSize: '2rem',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `${slide.color}15`,
                    borderRadius: '50%'
                  }}>{point.icon}</span>
                  <span style={{ 
                    color: '#333', 
                    fontSize: '1.1rem', 
                    fontWeight: '500' 
                  }}>{point.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'transvasement_complete':
        return (
          <div style={{ maxWidth: '1000px', margin: '0 auto', maxHeight: '70vh', overflowY: 'auto', padding: '0.5rem' }}>
            {/* Header avec image */}
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem' }}>
              {/* Image */}
              <div style={{ flex: '0 0 250px' }}>
                <img src={slide.image} alt={slide.title} style={{ width: '100%', borderRadius: '1rem', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }} />
              </div>
              {/* Titre et définition */}
              <div style={{ flex: 1 }}>
                <h2 style={{ color: slide.color, marginBottom: '0.5rem', fontSize: '1.3rem' }}>{slide.title}</h2>
                <p style={{ color: '#666', fontSize: '0.85rem', fontStyle: 'italic', marginBottom: '0.75rem' }}>{slide.subtitle}</p>
                
                <div style={{ padding: '0.75rem', background: `${slide.color}10`, borderRadius: '0.75rem', marginBottom: '0.5rem' }}>
                  <h4 style={{ color: slide.color, margin: '0 0 0.4rem', fontSize: '0.9rem' }}>🎯 Définition</h4>
                  <p style={{ color: '#444', fontSize: '0.8rem', margin: '0 0 0.4rem' }}>{slide.definition.text}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {slide.definition.materials.map((m, i) => (
                      <span key={i} style={{ padding: '0.2rem 0.5rem', background: 'white', borderRadius: '0.5rem', fontSize: '0.75rem', color: '#666' }}>• {m}</span>
                    ))}
                  </div>
                  <p style={{ color: '#888', fontSize: '0.75rem', margin: '0.4rem 0 0', fontStyle: 'italic' }}>{slide.definition.note}</p>
                </div>
              </div>
            </div>
            
            {/* Intérêts développementaux */}
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ color: slide.color, marginBottom: '0.5rem', fontSize: '1rem' }}>🧠 Intérêts développementaux</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem' }}>
                {slide.interests.map((interest, i) => (
                  <div key={i} style={{ padding: '0.6rem', background: 'white', borderRadius: '0.5rem', boxShadow: '0 2px 6px rgba(0,0,0,0.05)', borderLeft: `3px solid ${slide.color}` }}>
                    <h5 style={{ color: slide.color, margin: '0 0 0.3rem', fontSize: '0.8rem' }}>{interest.number} {interest.title}</h5>
                    {interest.intro && <p style={{ color: '#666', fontSize: '0.7rem', margin: '0 0 0.2rem', fontStyle: 'italic' }}>{interest.intro}</p>}
                    {interest.points.map((p, j) => (
                      <p key={j} style={{ color: '#555', fontSize: '0.7rem', margin: '0.1rem 0' }}>• {p}</p>
                    ))}
                    {interest.note && <p style={{ color: slide.color, fontSize: '0.7rem', margin: '0.3rem 0 0', fontWeight: '600' }}>{interest.note}</p>}
                    {interest.useful && (
                      <p style={{ color: '#888', fontSize: '0.65rem', margin: '0.2rem 0 0' }}>Utile : {interest.useful.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Adaptation selon l'âge */}
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ color: slide.color, marginBottom: '0.5rem', fontSize: '1rem' }}>👶 Adaptation selon l'âge</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                {slide.ageAdaptation.map((age, i) => (
                  <div key={i} style={{ padding: '0.75rem', background: `${slide.color}08`, borderRadius: '0.75rem' }}>
                    <div style={{ textAlign: 'center', marginBottom: '0.4rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>{age.emoji}</span>
                      <h4 style={{ color: slide.color, margin: '0.2rem 0', fontSize: '0.9rem' }}>{age.age}</h4>
                    </div>
                    <div style={{ marginBottom: '0.4rem' }}>
                      <span style={{ color: '#333', fontSize: '0.75rem', fontWeight: '600' }}>Matériel : </span>
                      <span style={{ color: '#666', fontSize: '0.75rem' }}>{age.material.join(', ')}</span>
                    </div>
                    <div>
                      <span style={{ color: '#333', fontSize: '0.75rem', fontWeight: '600' }}>Objectif : </span>
                      <span style={{ color: '#666', fontSize: '0.75rem' }}>{age.objective.join(', ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Rôle AP et Aménagement */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {/* Rôle AP */}
              <div style={{ padding: '0.75rem', background: `linear-gradient(135deg, ${slide.color}15, ${slide.color}05)`, borderRadius: '0.75rem' }}>
                <h4 style={{ color: slide.color, margin: '0 0 0.5rem', fontSize: '0.9rem' }}>👩‍⚕️ Rôle professionnel</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem' }}>
                  {slide.roleAP.actions.map((action, i) => (
                    <span key={i} style={{ padding: '0.25rem 0.5rem', background: 'white', borderRadius: '0.5rem', fontSize: '0.75rem', color: '#555' }}>✓ {action}</span>
                  ))}
                </div>
                <p style={{ color: '#666', fontSize: '0.8rem', fontStyle: 'italic', margin: 0 }}>Exemple : {slide.roleAP.example}</p>
              </div>
              
              {/* Aménagement */}
              <div style={{ padding: '0.75rem', background: 'white', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <h4 style={{ color: slide.color, margin: '0 0 0.5rem', fontSize: '0.9rem' }}>🏠 Aménagement de l'espace</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem' }}>
                  {slide.amenagement.items.map((item, i) => (
                    <span key={i} style={{ padding: '0.25rem 0.5rem', background: `${slide.color}10`, borderRadius: '0.5rem', fontSize: '0.75rem', color: '#555' }}>• {item}</span>
                  ))}
                </div>
                <p style={{ color: slide.color, fontSize: '0.8rem', fontWeight: '600', margin: 0, padding: '0.4rem', background: '#fff3cd', borderRadius: '0.4rem' }}>⚠️ {slide.amenagement.important}</p>
              </div>
            </div>
          </div>
        );

      case 'social_skills_complete':
        return (
          <div style={{ maxWidth: '1000px', margin: '0 auto', maxHeight: '70vh', overflowY: 'auto', padding: '0.5rem' }}>
            {/* Titre et définition */}
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <h2 style={{ color: slide.color, marginBottom: '0.5rem' }}>{slide.title}</h2>
              <p style={{ color: '#444', fontSize: '0.95rem', fontStyle: 'italic', maxWidth: '800px', margin: '0 auto 0.5rem' }}>
                {slide.definition}
              </p>
              <p style={{ color: slide.color, fontWeight: '600', fontSize: '0.9rem' }}>{slide.notInnate}</p>
            </div>
            
            {/* Les 7 compétences - grille 2 colonnes */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
              {slide.skills.map((skill, index) => (
                <div key={index} style={{ 
                  padding: '0.75rem', 
                  background: 'white', 
                  borderRadius: '0.75rem', 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  borderLeft: `4px solid ${slide.color}`
                }}>
                  <h4 style={{ color: slide.color, margin: '0 0 0.5rem', fontSize: '0.9rem' }}>
                    {skill.number} {skill.title}
                  </h4>
                  {skill.intro && <p style={{ color: '#666', fontSize: '0.75rem', margin: '0 0 0.4rem', fontStyle: 'italic' }}>{skill.intro}</p>}
                  {skill.toDevelop && (
                    <div style={{ marginBottom: '0.3rem' }}>
                      <span style={{ color: '#333', fontSize: '0.75rem', fontWeight: '600' }}>À développer : </span>
                      <span style={{ color: '#666', fontSize: '0.75rem' }}>{skill.toDevelop.join(' • ')}</span>
                    </div>
                  )}
                  {skill.roleAP && (
                    <div style={{ marginBottom: '0.3rem' }}>
                      <span style={{ color: slide.color, fontSize: '0.75rem', fontWeight: '600' }}>Rôle de l'AP : </span>
                      <span style={{ color: '#666', fontSize: '0.75rem' }}>{skill.roleAP.join(' • ')}</span>
                    </div>
                  )}
                  {skill.objective && <p style={{ color: '#666', fontSize: '0.75rem', margin: '0.2rem 0' }}>🎯 {skill.objective}</p>}
                  {skill.accompagnement && (
                    <div>
                      <span style={{ color: '#333', fontSize: '0.75rem', fontWeight: '600' }}>Accompagnement : </span>
                      <span style={{ color: '#666', fontSize: '0.75rem' }}>{skill.accompagnement.join(' • ')}</span>
                    </div>
                  )}
                  {skill.benefits && (
                    <div>
                      <span style={{ color: '#333', fontSize: '0.75rem', fontWeight: '600' }}>Ils permettent d'apprendre : </span>
                      <span style={{ color: '#666', fontSize: '0.75rem' }}>{skill.benefits.join(' • ')}</span>
                    </div>
                  )}
                  {skill.moyens && (
                    <div>
                      <span style={{ color: '#333', fontSize: '0.75rem', fontWeight: '600' }}>Moyens : </span>
                      <span style={{ color: '#666', fontSize: '0.75rem' }}>{skill.moyens.join(' • ')}</span>
                    </div>
                  )}
                  {skill.childLearns && (
                    <div style={{ marginBottom: '0.3rem' }}>
                      <span style={{ color: '#333', fontSize: '0.75rem', fontWeight: '600' }}>L'enfant apprend : </span>
                      <span style={{ color: '#666', fontSize: '0.75rem' }}>{skill.childLearns.join(' • ')}</span>
                    </div>
                  )}
                  {skill.rulesMustBe && (
                    <div>
                      <span style={{ color: slide.color, fontSize: '0.75rem', fontWeight: '600' }}>Les règles doivent être : </span>
                      <span style={{ color: '#666', fontSize: '0.75rem' }}>✔ {skill.rulesMustBe.join(' ✔ ')}</span>
                    </div>
                  )}
                  {skill.activities && (
                    <div style={{ marginBottom: '0.3rem' }}>
                      <span style={{ color: '#333', fontSize: '0.75rem', fontWeight: '600' }}>Activités : </span>
                      <span style={{ color: '#666', fontSize: '0.75rem' }}>{skill.activities.join(' • ')}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Évolution selon l'âge */}
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ color: slide.color, textAlign: 'center', marginBottom: '0.5rem', fontSize: '1rem' }}>📈 Évolution selon l'âge</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                {slide.ageEvolution.map((age, index) => (
                  <div key={index} style={{ 
                    padding: '0.75rem', 
                    background: `${slide.color}08`, 
                    borderRadius: '0.75rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{age.emoji}</div>
                    <h4 style={{ color: slide.color, margin: '0 0 0.4rem', fontSize: '0.85rem' }}>{age.age}</h4>
                    {age.skills.map((s, i) => (
                      <p key={i} style={{ color: '#666', fontSize: '0.75rem', margin: '0.15rem 0' }}>• {s}</p>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Rôle de l'AP */}
            <div style={{ 
              padding: '1rem', 
              background: `linear-gradient(135deg, ${slide.color}15, ${slide.color}05)`, 
              borderRadius: '0.75rem',
              textAlign: 'center'
            }}>
              <h3 style={{ color: slide.color, margin: '0 0 0.5rem', fontSize: '1rem' }}>
                {slide.roleAP.emoji} {slide.roleAP.title}
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                {slide.roleAP.actions.map((action, i) => (
                  <span key={i} style={{ 
                    padding: '0.3rem 0.75rem', 
                    background: 'white', 
                    borderRadius: '1rem', 
                    color: '#333', 
                    fontSize: '0.8rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    ✓ {action}
                  </span>
                ))}
              </div>
              <p style={{ color: slide.color, fontWeight: '600', fontSize: '0.9rem', margin: 0 }}>{slide.roleAP.conclusion}</p>
            </div>
          </div>
        );

      default:
        return (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: slide.color || '#333' }}>{slide.title}</h2>
            {slide.subtitle && <p style={{ color: '#666' }}>{slide.subtitle}</p>}
          </div>
        );
    }
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
        padding: '1rem 2rem',
        borderRadius: '1rem 1rem 0 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white'
      }}>
        <span>Activité 1 bis - DEAP</span>
        <span>{currentSlide + 1} / {SLIDES.length}</span>
      </div>

      {/* Content */}
      <div style={{
        background: 'white',
        padding: '2.5rem 2rem',
        minHeight: '450px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {renderSlideContent()}
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '1rem',
        padding: '1.5rem',
        background: '#f8f9fa',
        borderRadius: '0 0 1rem 1rem'
      }}>
        <button
          onClick={handlePrevious}
          disabled={currentSlide === 0}
          style={{
            padding: '0.75rem 1.5rem',
            borderRadius: '2rem',
            border: '2px solid #8B5CF6',
            background: 'white',
            color: '#8B5CF6',
            cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
            opacity: currentSlide === 0 ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <ChevronLeft size={20} /> Précédent
        </button>

        <button
          onClick={handleSpeak}
          disabled={isPlaying || !slide.narration}
          style={{
            padding: '0.75rem 1.5rem',
            borderRadius: '2rem',
            border: 'none',
            background: '#8B5CF6',
            color: 'white',
            cursor: isPlaying ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Volume2 size={20} /> {isPlaying ? 'Lecture...' : 'Écouter'}
        </button>

        <button
          onClick={handleNext}
          disabled={currentSlide === SLIDES.length - 1}
          style={{
            padding: '0.75rem 1.5rem',
            borderRadius: '2rem',
            border: 'none',
            background: '#8B5CF6',
            color: 'white',
            cursor: currentSlide === SLIDES.length - 1 ? 'not-allowed' : 'pointer',
            opacity: currentSlide === SLIDES.length - 1 ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          Suivant <ChevronRight size={20} />
        </button>
      </div>

      {/* Progress Bar */}
      <div style={{ marginTop: '1rem' }}>
        <div style={{
          height: '6px',
          background: '#e0e0e0',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${((currentSlide + 1) / SLIDES.length) * 100}%`,
            background: 'linear-gradient(90deg, #8B5CF6 0%, #A78BFA 100%)',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>
    </div>
  );
};

export default DEAPActivite1bisPresentation;
